// frontend/src/components/AssetDetailModal.js
/**
 * Asset Detail Modal - Category-Aware Design
 * 
 * Displays asset details with category-specific sections:
 * - Fleet (FLT): Vehicle info, regulatory expiries, insurance
 * - Software Licenses (LIC): License details, subscription, seat management
 * - IT Equipment (IT): Specs, warranty, purchase info
 * - Access Control (ACC): Activation status
 * - Default: Base asset info
 * 
 * Theme: Red (Admin)
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { pluralizeDays } from '../utils/numberUtils';
import {
  X, Package, Car, Laptop, Key, FileKey, Clock, AlertCircle, AlertTriangle,
  User, Calendar, MapPin, Shield, ShieldCheck, ShieldOff, CreditCard, Users, CheckCircle,
  Truck, Building2, FileText, Settings, Fuel, Link, Eye, EyeOff, Image, ExternalLink,
  Mail, MailWarning, RefreshCw, ChevronLeft, Wrench, Gauge, ClipboardCheck,
  ShieldAlert, Siren, Smartphone,
} from 'lucide-react';
import toast from '../utils/toast';
import assetInventoryApi from '../services/api';
import {
  useAsset, useAssetHistory, useMaintenanceLogs, useVehicleInspections,
  useTrafficOffences, useVehicleAccidents, useAccidentVehicleStats,
  useSyncFleetVehicleCompliance,
} from '../hooks/useAssetInventoryData';
import { getCategoryColorByCode } from '../utils/assetStatusColors';
import { deviceLineContext, isDeviceMobileLine } from '../utils/mobileLineDisplay';
import { StatusBadge } from './UI/StatusBadge';
import { InfoBadge } from './UI/InfoBadge';
import { CountBadge } from './UI/CountBadge';
import { ClassificationBadge } from './UI/ClassificationBadge';
import { Pagination } from './UI/Pagination';
import QRCodeDisplay from './QRCodeDisplay';
import Modal from './UI/Modal';
import LetterPreviewModal from './LetterPreviewModal';
import VehicleInspectionDetailModal from './asset/VehicleInspectionDetailModal';
import TrafficOffenceDetailModal from './asset/TrafficOffenceDetailModal';
import AccidentDetailModal, {
  accidentBadgeStatusFor,
  claimBadgeStatusFor,
} from './asset/AccidentDetailModal';
import SecurePhotoThumb from './asset/SecurePhotoThumb';
import { useAuth } from '../contexts/AuthContext';

import { formatDate, formatDateTimeCompact, formatRelativeTime } from '../utils/dateUtils';
import { getAssetHeaderMetaVisibility } from '../utils/assetHeaderMeta';
import {
  formatEmployeeParty,
  formatTransferLocationRoute,
  formatTransferRoute,
} from '../utils/transferDisplayUtils';
import { resolveTransferTypeLabel } from '../utils/transferTypeMeta';

// ============================================
// UTILITY FUNCTIONS
// ============================================

const formatCurrency = (amount) => {
  if (!amount) return '—';
  return new Intl.NumberFormat('en-SG', {
    style: 'currency',
    currency: 'SGD',
  }).format(amount);
};

const formatInteger = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  return Number(value).toLocaleString('en-SG');
};

const formatEngineOutput = (fleet) => {
  const parts = [];
  if (fleet?.power_output_kw) parts.push(`${fleet.power_output_kw} kW`);
  if (fleet?.power_output_bhp) parts.push(`${fleet.power_output_bhp} bhp`);
  return parts.length > 0 ? parts.join(' / ') : '—';
};

/**
 * Human-friendly label for custom_fields keys (e.g. ram_gb → "RAM (GB)").
 * Known IT/asset keys from asset type schemas; unknown keys are title-cased.
 */
const CUSTOM_FIELD_LABELS = {
  ram_gb: 'RAM (GB)',
  os_version: 'OS Version',
  storage_gb: 'Storage (GB)',
  cpu: 'CPU',
  size_inches: 'Size (inches)',
  resolution: 'Resolution',
};
const formatCustomFieldLabel = (key) => {
  if (CUSTOM_FIELD_LABELS[key]) return CUSTOM_FIELD_LABELS[key];
  return String(key).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

// custom_fields keys owned by the Trend Micro importer. These are rendered in a
// dedicated "Endpoint Protection" section, so they're excluded from the generic
// Specifications list to avoid showing raw values like "Trendmicro Installed: true".
const TRENDMICRO_CUSTOM_FIELD_KEYS = [
  'trendmicro_installed',
  'trendmicro_install_date',
  'trendmicro_endpoint',
  'trendmicro_synced_at',
];

/**
 * Age from purchase_date: "X years, Y months" or "Y months". short: "Xy Ym" / "Ym".
 * Uses date-only comparison; returns "—" if no purchase_date.
 */
const formatAgeYearsMonths = (purchaseDate, { short = false } = {}) => {
  if (!purchaseDate) return '—';
  const start = new Date(purchaseDate);
  const end = new Date();
  const startY = start.getFullYear(), startM = start.getMonth(), startD = start.getDate();
  const endY = end.getFullYear(), endM = end.getMonth(), endD = end.getDate();
  let months = (endY - startY) * 12 + (endM - startM);
  if (endD < startD) months -= 1;
  if (months < 0) months = 0;
  const years = Math.floor(months / 12);
  const m = months % 12;
  if (short) {
    if (years > 0) return m > 0 ? `${years}y ${m}m` : `${years}y`;
    return m > 0 ? `${m}m` : '< 1m';
  }
  if (years > 0) return m > 0 ? `${years} years, ${m} months` : `${years} year${years !== 1 ? 's' : ''}`;
  return m > 0 ? `${m} month${m !== 1 ? 's' : ''}` : '< 1 month';
};

/**
 * Human-friendly length of a custody segment (gap between two timeline events).
 * Day-precise for short stints (so workshop turnaround / idle pool time is
 * obvious), folding into months/years once it's long. Returns null for an
 * invalid or negative interval.
 */
const formatDurationShort = (startDate, endDate) => {
  if (!startDate || !endDate) return null;
  const start = new Date(startDate);
  const end = new Date(endDate);
  const ms = end - start;
  if (Number.isNaN(ms) || ms < 0) return null;
  const days = Math.floor(ms / 86400000);
  if (days === 0) {
    // Sub-day stint — the legacy ledger carries real timestamps, so show hours
    // /minutes (e.g. workshop drop-off → pick-up the same morning).
    const mins = Math.floor(ms / 60000);
    if (mins < 1) return '< 1 min';
    if (mins < 60) return `${mins} min`;
    const hrs = Math.floor(mins / 60);
    const rem = mins % 60;
    return rem > 0 ? `${hrs}h ${rem}m` : `${hrs}h`;
  }
  if (days < 60) return `${days} ${pluralizeDays(days)}`;
  let months = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth());
  if (end.getDate() < start.getDate()) months -= 1;
  const years = Math.floor(months / 12);
  const m = months % 12;
  if (years > 0) return m > 0 ? `${years}y ${m}m` : `${years}y`;
  return `${m}m`;
};

/**
 * What happened to the asset for the period that BEGINS at this event, used to
 * label the custody-duration chip. Returns null for lifecycle-end / metadata
 * events (disposal, warranty, access) where a "held for" duration is noise.
 */
const getSegmentMeta = (event) => {
  if (event.type === 'transfer') {
    switch (event.transfer_type) {
      case 'repair':
        return { verb: 'At workshop', tone: 'amber' };
      case 'return':
        return { verb: 'In inventory', tone: 'gray' };
      case 'disposal':
      case 'termination':
      case 'lost':
        return null;
      case 'repair_return':
        return event.to_employee_name
          ? { verb: 'Held', tone: 'blue' }
          : { verb: 'In inventory', tone: 'gray' };
      default:
        // initial / transfer / deployment / found / offboarding
        return event.to_employee_name
          ? { verb: 'Held', tone: 'blue' }
          : { verb: 'In inventory', tone: 'gray' };
    }
  }
  if (event.type === 'purchase' || event.type === 'created') return { verb: 'In inventory', tone: 'gray' };
  if (event.type === 'assignment') return { verb: 'Held', tone: 'blue' };
  return null;
};

/**
 * Precise instant for sorting / duration math. Transfers carry a full
 * `transfer_date` timestamp; other events only have a calendar date. The
 * displayed date label still uses event.date (the local effective date) so the
 * UTC timestamp can't shift the shown day.
 */
const eventInstant = (event) => new Date(event?.transfer_date || event?.date);

// Workshop moves (vehicle to/from a garage) never involve an acknowledgement
// letter — those are only for employee custody changes.
const WORKSHOP_MOVE_TYPES = ['repair', 'repair_return'];

/**
 * Days until a date using date-only comparison (local time).
 * Ensures the end date itself is not treated as overdue: when due date === today, returns 0.
 */
const getDaysUntil = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  const today = new Date();
  const dueDateOnly = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const todayOnly = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  return Math.ceil((dueDateOnly - todayOnly) / (1000 * 60 * 60 * 24));
};

/**
 * Format days into human-readable years, months, days
 * e.g., 1473 days -> "4y 0m 18d"
 */
const formatDaysToYMD = (totalDays) => {
  const absDays = Math.abs(totalDays);
  const years = Math.floor(absDays / 365);
  const remainingAfterYears = absDays % 365;
  const months = Math.floor(remainingAfterYears / 30);
  const days = remainingAfterYears % 30;
  
  const parts = [];
  if (years > 0) parts.push(`${years}y`);
  if (months > 0 || years > 0) parts.push(`${months}m`);
  parts.push(`${days}d`);
  
  return parts.join(' ');
};

// Statuses that indicate the asset is no longer active. We split these
// into TRUE disposal (recovery / scrap / write-off) and non-disposal
// lifecycle ends (subscription/contract terminated, access deactivated)
// because they need different banners and copy.
const DISPOSAL_STATUSES = ['scrapped', 'sold', 'written_off', 'disposed'];
const TERMINATION_STATUSES = ['terminated', 'deactivated'];
const LEASE_RETURN_STATUSES = ['returned_to_lessor'];
const INACTIVE_STATUSES = [...DISPOSAL_STATUSES, ...TERMINATION_STATUSES, ...LEASE_RETURN_STATUSES];

/**
 * @param {string} dateStr - ISO date string
 * @param {boolean} isDisposed - hide badge for disposed assets
 * @param {{ variant?: 'actionable'|'warranty' }} opts - 'actionable' (default): past = "X overdue";
 *   'warranty': past = "Expired" (grey). Warranty end is informational, not a renewal.
 */
const getExpiryBadge = (dateStr, isDisposed = false, { variant = 'actionable' } = {}) => {
  // Don't show countdown badges for disposed assets
  if (isDisposed) return null;
  
  const days = getDaysUntil(dateStr);
  if (days === null) return null;
  
  // End date is the last valid day: days < 0 = past; days === 0 = expires today (still valid)
  const isPast = days < 0;
  const isUrgent = days >= 1 && days <= 30;
  const isWarning = days > 30 && days <= 90;
  const isLastDay = days === 0;
  
  // Warranty past: show "Expired" (neutral). "Overdue" implies action; warranty simply ends.
  if (variant === 'warranty' && isPast) {
    return (
      <InfoBadge color="gray" label="Expired" />
    );
  }

  // Lock-in end is not a disconnection. A past date means the line is
  // still billed and free to re-contract, port, or cancel.
  if (variant === 'contract' && isPast) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
        <AlertTriangle className="w-3.5 h-3.5" />
        Out of contract
      </span>
    );
  }
  
  let bgColor, textColor, icon, label;
  if (isPast) {
    // Actionable only: renewal/action is late
    bgColor = 'bg-red-100'; textColor = 'text-red-700';
    icon = <AlertCircle className="w-3.5 h-3.5" />;
    label = `${formatDaysToYMD(days)} overdue`;
  } else if (isLastDay) {
    bgColor = 'bg-green-100'; textColor = 'text-green-700';
    icon = <CheckCircle className="w-3.5 h-3.5" />;
    label = 'Expires today';
  } else if (isUrgent) {
    bgColor = 'bg-amber-100'; textColor = 'text-amber-700';
    icon = <AlertTriangle className="w-3.5 h-3.5" />;
    label = `${formatDaysToYMD(days)} left`;
  } else if (isWarning) {
    bgColor = 'bg-yellow-100'; textColor = 'text-yellow-700';
    icon = <Clock className="w-3.5 h-3.5" />;
    label = `${formatDaysToYMD(days)} left`;
  } else {
    bgColor = 'bg-green-100'; textColor = 'text-green-700';
    icon = <CheckCircle className="w-3.5 h-3.5" />;
    label = `${formatDaysToYMD(days)} left`;
  }
  
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${bgColor} ${textColor}`}>
      {icon}
      {label}
    </span>
  );
};

// ============================================
// DETAIL FIELD COMPONENT
// ============================================

const DetailField = ({ label, value, className = '' }) => (
  <div className={className}>
    <dt className="text-xs text-gray-500 font-medium uppercase tracking-wide">{label}</dt>
    <dd className="mt-1 text-sm text-gray-900">{value || '—'}</dd>
  </div>
);

const DetailFieldWithBadge = ({ label, value, badge }) => (
  <div>
    <dt className="text-xs text-gray-500 font-medium uppercase tracking-wide">{label}</dt>
    <dd className="mt-1 flex items-center gap-2">
      <span className="text-sm text-gray-900">{value || '—'}</span>
      {badge}
    </dd>
  </div>
);

const ToggleablePINField = ({ label, pin }) => {
  const [isVisible, setIsVisible] = useState(false);
  
  if (!pin) {
    return <DetailField label={label} value="—" />;
  }
  
  return (
    <div>
      <dt className="text-xs text-gray-500 font-medium uppercase tracking-wide">{label}</dt>
      <dd className="mt-1 flex items-center gap-2">
        <span className="text-sm text-gray-900 font-mono">
          {isVisible ? pin : '••••'}
        </span>
        <button
          type="button"
          onClick={() => setIsVisible(!isVisible)}
          className="p-1 hover:bg-gray-100 rounded transition-colors"
          title={isVisible ? 'Hide PIN' : 'Show PIN'}
        >
          {isVisible ? (
            <EyeOff className="w-4 h-4 text-gray-500" />
          ) : (
            <Eye className="w-4 h-4 text-gray-500" />
          )}
        </button>
      </dd>
    </div>
  );
};

// ============================================
// SECTION COMPONENT
// ============================================

const Section = ({ title, icon: Icon, children, className = '' }) => (
  <div className={`bg-gray-50 rounded-xl p-3 ${className}`}>
    <h4 className="flex items-center gap-2 text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
      {Icon && <Icon className="w-3.5 h-3.5" />}
      {title}
    </h4>
    <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
      {children}
    </dl>
  </div>
);

// ============================================
// FLEET SERVICE HISTORY
// ============================================

const MAINTENANCE_TYPE_COLOR = {
  repair: 'red',
  preventive: 'green',
  inspection: 'blue',
  upgrade: 'violet',
  cleaning: 'cyan',
};

/**
 * Workshop service history for a fleet vehicle, sourced from AssetMaintenanceLog.
 * Read-only timeline: date, type, workshop, odometer and the parts serviced.
 * Self-contained (owns its own query) so it can be dropped into the detail modal.
 */
const SERVICE_HISTORY_PAGE_SIZE = 6;

const FleetServiceHistory = ({ assetId }) => {
  const [page, setPage] = useState(1);
  const { data, isLoading, isError } = useMaintenanceLogs(
    assetId ? { asset: assetId, ordering: '-start_date', page_size: 200 } : {}
  );

  const logs = data?.results || [];

  // Roll the cleaned per-visit components up into a vehicle-level demand
  // profile (component -> number of visits it appeared on), most-frequent first.
  // Computed over ALL visits, independent of the current list page.
  const componentSummary = useMemo(() => {
    const counts = new Map();
    for (const log of logs) {
      for (const part of log.parts_serviced_display || []) {
        counts.set(part, (counts.get(part) || 0) + 1);
      }
    }
    return [...counts.entries()].sort((a, b) => b[1] - a[1]);
  }, [logs]);

  const totalPages = Math.max(1, Math.ceil(logs.length / SERVICE_HISTORY_PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pagedLogs = logs.slice(
    (currentPage - 1) * SERVICE_HISTORY_PAGE_SIZE,
    currentPage * SERVICE_HISTORY_PAGE_SIZE
  );

  if (!assetId) return null;

  return (
    <div className="space-y-3">
      <div className="bg-gray-50 rounded-xl p-3">
        <h4 className="flex items-center gap-2 text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
          <Wrench className="w-3.5 h-3.5" />
          Service History
          {logs.length > 0 && (
            <CountBadge count={logs.length} theme="admin" size="sm" />
          )}
        </h4>

        {/* Vehicle-level cleaned component profile, aggregated from all visits. */}
        {componentSummary.length > 0 && (
          <div className="mb-3 rounded-lg border border-gray-200 bg-white p-2.5">
            <p className="mb-1.5 text-[10px] font-semibold uppercase tracking-wide text-gray-400">
              Components Serviced
            </p>
            <div className="flex flex-wrap gap-1.5">
              {componentSummary.map(([name, count]) => (
                <span
                  key={name}
                  className="inline-flex items-center gap-1.5 rounded-full bg-gray-100 px-2.5 py-1 text-[11px] font-medium text-gray-700"
                >
                  {name}
                  <CountBadge count={count} theme="neutral" size="sm" />
                </span>
              ))}
            </div>
          </div>
        )}

        {isLoading ? (
          <p className="text-sm text-gray-400 py-2">Loading service history…</p>
        ) : isError ? (
          <p className="text-sm text-red-500 py-2">Could not load service history.</p>
        ) : logs.length === 0 ? (
          <p className="text-sm text-gray-400 py-2">No service records yet.</p>
        ) : (
          <ul className="space-y-2">
            {pagedLogs.map((log) => {
              const workshop = log.workshop_name || log.vendor || '—';
              const parts = log.parts_serviced_display || [];
              return (
                <li
                  key={log.id}
                  className="bg-white rounded-lg border border-gray-200 px-3 py-2"
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-sm font-medium text-gray-900 whitespace-nowrap">
                        {formatDate(log.start_date)}
                      </span>
                      <InfoBadge
                        color={MAINTENANCE_TYPE_COLOR[log.maintenance_type] || 'gray'}
                        label={log.maintenance_type_display || log.maintenance_type}
                        size="xs"
                      />
                    </div>
                    {log.mileage != null && (
                      <span className="flex items-center gap-1 text-xs text-gray-500 whitespace-nowrap">
                        <Gauge className="w-3 h-3" />
                        {formatInteger(log.mileage)} km
                      </span>
                    )}
                  </div>

                  <div className="mt-1 flex items-center gap-1.5 text-xs text-gray-600">
                    <Building2 className="w-3 h-3 flex-shrink-0 text-gray-400" />
                    <span className="truncate">{workshop}</span>
                  </div>

                  {/* Show the raw description only when no normalized parts were
                      matched — otherwise the chips below already convey it
                      (and showing both duplicates the same info). */}
                  {parts.length === 0 && log.description && log.description !== 'Service' && (
                    <p className="mt-1 text-xs text-gray-500 truncate" title={log.description}>
                      {log.description}
                    </p>
                  )}

                  {parts.length > 0 && (
                    <div className="mt-1.5 flex flex-wrap gap-1">
                      {parts.map((p, idx) => (
                        <span
                          key={idx}
                          className="inline-flex items-center rounded bg-gray-100 px-1.5 py-0.5 text-[10px] text-gray-600"
                        >
                          {p}
                        </span>
                      ))}
                    </div>
                  )}

                  {(log.next_service_mileage != null || log.tyre_pressure_psi != null) && (
                    <div className="mt-1.5 flex flex-wrap gap-x-4 gap-y-0.5 text-[10px] text-gray-400">
                      {log.next_service_mileage != null && (
                        <span>Next due: {formatInteger(log.next_service_mileage)} km</span>
                      )}
                      {log.tyre_pressure_psi != null && (
                        <span>Tyre: {log.tyre_pressure_psi} PSI</span>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setPage}
        totalCount={logs.length}
        pageSize={SERVICE_HISTORY_PAGE_SIZE}
        theme="admin"
        noun="visits"
      />
    </div>
  );
};

// ============================================
// FLEET MAINTENANCE TAB (odometer snapshot + service history)
// ============================================

/**
 * Dedicated "Maintenance History" tab body for fleet vehicles. Leads with the
 * latest odometer / next-service snapshot (cached on the vehicle), then the
 * full imported workshop service history. Kept separate from the Overview tab
 * so maintenance is a first-class destination an ops manager can jump straight to.
 */
const FleetMaintenanceTab = ({ fleet, asset, isDisposed = false }) => {
  if (!fleet) {
    return (
      <div className="text-center py-8">
        <Wrench className="w-12 h-12 text-gray-300 mx-auto mb-3" />
        <p className="text-gray-500">No vehicle record</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Odometer & service-interval snapshot (latest values cached on the vehicle). */}
      <Section title="Service & Odometer" icon={Gauge}>
        <DetailField
          label="Current Mileage"
          value={fleet.current_mileage != null ? `${formatInteger(fleet.current_mileage)} km` : '—'}
        />
        <DetailField label="As Of" value={formatDate(fleet.current_mileage_date)} />
        <DetailField
          label="Next Service Due"
          value={fleet.next_service_mileage != null ? `${formatInteger(fleet.next_service_mileage)} km` : '—'}
        />
        <DetailFieldWithBadge
          label="Distance To Service"
          value={
            fleet.mileage_to_next_service != null
              ? `${formatInteger(Math.abs(fleet.mileage_to_next_service))} km ${fleet.mileage_to_next_service < 0 ? 'overdue' : 'left'}`
              : '—'
          }
          badge={
            !isDisposed && fleet.is_service_due_soon ? (
              <InfoBadge
                color={fleet.mileage_to_next_service < 0 ? 'red' : 'amber'}
                label={fleet.mileage_to_next_service < 0 ? 'Overdue' : 'Due soon'}
                size="xs"
              />
            ) : null
          }
        />
      </Section>

      {/* Full workshop service history imported from the fleet maintenance log. */}
      <FleetServiceHistory assetId={asset?.id} />
    </div>
  );
};

// ============================================
// FLEET INSPECTION HISTORY TAB
// ============================================

const INSPECTION_HISTORY_PAGE_SIZE = 6;

/**
 * Dedicated "Inspection History" tab for fleet vehicles — the monthly driver
 * walk-around log for THIS vehicle. Ops manager questions answered here:
 *   - Has this vehicle been inspected this month?
 *   - What was flagged last time, and by whom?
 *   - Is there a recurring pattern of exceptions?
 * Sibling to Maintenance History (workshop) and Transaction History (custody).
 */
const FleetInspectionHistoryTab = ({ fleet }) => {
  const [page, setPage] = useState(1);
  const [detailModal, setDetailModal] = useState({ open: false, inspectionId: null });
  const fleetVehicleId = fleet?.id;

  const { data: inspections = [], isLoading, isError } = useVehicleInspections(
    fleetVehicleId
      ? { fleet_vehicle: fleetVehicleId, ordering: '-inspection_date', page_size: 200 }
      : {},
    { enabled: !!fleetVehicleId }
  );

  const flaggedCount = useMemo(
    () => inspections.filter((i) => i.has_exceptions || i.status === 'flagged').length,
    [inspections]
  );

  const totalPages = Math.max(1, Math.ceil(inspections.length / INSPECTION_HISTORY_PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paged = inspections.slice(
    (currentPage - 1) * INSPECTION_HISTORY_PAGE_SIZE,
    currentPage * INSPECTION_HISTORY_PAGE_SIZE
  );

  if (!fleet) {
    return (
      <div className="text-center py-8">
        <ClipboardCheck className="w-12 h-12 text-gray-300 mx-auto mb-3" />
        <p className="text-gray-500">No vehicle record</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Glanceable summary — mirrors the Transaction History stats strip. */}
      <div className="bg-gray-50 rounded-xl p-3 grid grid-cols-3 gap-2 text-center">
        <div>
          <p className="text-lg font-bold text-gray-900">{inspections.length}</p>
          <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">Inspections</p>
        </div>
        <div>
          <p className={`text-lg font-bold ${flaggedCount > 0 ? 'text-red-600' : 'text-gray-900'}`}>
            {flaggedCount}
          </p>
          <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">Flagged</p>
        </div>
        <div>
          <p className="text-lg font-bold text-gray-900">
            {inspections[0]?.inspection_date ? formatDate(inspections[0].inspection_date) : '—'}
          </p>
          <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">Latest</p>
        </div>
      </div>

      <div className="bg-gray-50 rounded-xl p-3">
        <h4 className="flex items-center gap-2 text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
          <ClipboardCheck className="w-3.5 h-3.5" />
          Monthly Walk-Around Log
          {inspections.length > 0 && (
            <span className="ml-1 normal-case text-gray-400">({inspections.length})</span>
          )}
        </h4>

        {isLoading ? (
          <p className="text-sm text-gray-400 py-2">Loading inspection history…</p>
        ) : isError ? (
          <p className="text-sm text-red-500 py-2">Could not load inspection history.</p>
        ) : inspections.length === 0 ? (
          <p className="text-sm text-gray-400 py-2">No inspections recorded yet.</p>
        ) : (
          <ul className="space-y-2">
            {paged.map((insp) => (
              <li key={insp.id}>
                <button
                  type="button"
                  onClick={() => setDetailModal({ open: true, inspectionId: insp.id })}
                  className="w-full text-left bg-white rounded-lg border border-gray-200 px-3 py-2 hover:border-gray-300 hover:shadow-sm transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-1"
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-sm font-medium text-gray-900 whitespace-nowrap">
                        {formatDate(insp.inspection_date)}
                      </span>
                      <StatusBadge status={insp.status} label={insp.status_display} />
                    </div>
                    {insp.odometer_km != null && (
                      <span className="flex items-center gap-1 text-xs text-gray-500 whitespace-nowrap">
                        <Gauge className="w-3 h-3" />
                        {formatInteger(insp.odometer_km)} km
                      </span>
                    )}
                  </div>
                  <div className="mt-1 flex items-center gap-1.5 text-xs text-gray-600">
                    <User className="w-3 h-3 flex-shrink-0 text-gray-400" />
                    <span className="truncate">
                      {insp.submitted_by_name || insp.assignee_name || '—'}
                    </span>
                    {insp.period_month && (
                      <>
                        <span className="text-gray-300">·</span>
                        <span className="text-gray-400 whitespace-nowrap">
                          {new Date(insp.period_month).toLocaleDateString('en-SG', {
                            month: 'short',
                            year: 'numeric',
                          })}
                        </span>
                      </>
                    )}
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setPage}
        totalCount={inspections.length}
        pageSize={INSPECTION_HISTORY_PAGE_SIZE}
        theme="admin"
        noun="inspections"
      />

      <VehicleInspectionDetailModal
        isOpen={detailModal.open}
        onClose={() => setDetailModal({ open: false, inspectionId: null })}
        inspectionId={detailModal.inspectionId}
      />
    </div>
  );
};

// ============================================
// FLEET TRAFFIC OFFENCE HISTORY TAB
// ============================================

const OFFENCE_HISTORY_PAGE_SIZE = 6;

// `closed` is globally a warning-tier status; a resolved offence is a success.
const offenceBadgeStatus = (status) => (status === 'closed' ? 'offence_closed' : status);

const ACCIDENT_HISTORY_PAGE_SIZE = 6;

/**
 * Dedicated "Accidents" tab for fleet vehicles — every accident record against
 * THIS plate. Deadline-driven insurer work lives on /asset-admin/fleet-accidents.
 */
const FleetAccidentHistoryTab = ({ fleet }) => {
  const [page, setPage] = useState(1);
  const [detailAccident, setDetailAccident] = useState(null);
  const fleetVehicleId = fleet?.id;

  const { data: accidents = [], isLoading, isError } = useVehicleAccidents(
    fleetVehicleId
      ? { fleet_vehicle: fleetVehicleId, include_closed: 'true', page_size: 200 }
      : {},
    { enabled: !!fleetVehicleId }
  );
  const { data: stats } = useAccidentVehicleStats(fleetVehicleId);

  const openCount = stats?.open ?? accidents.filter(
    (a) => !['accident_closed', 'cancelled'].includes(a.status)
  ).length;
  const totalClaimed = Number(stats?.total_claimed || 0);

  const totalPages = Math.max(1, Math.ceil(accidents.length / ACCIDENT_HISTORY_PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paged = accidents.slice(
    (currentPage - 1) * ACCIDENT_HISTORY_PAGE_SIZE,
    currentPage * ACCIDENT_HISTORY_PAGE_SIZE
  );

  if (!fleet) {
    return (
      <div className="text-center py-8">
        <Siren className="w-12 h-12 text-gray-300 mx-auto mb-3" />
        <p className="text-gray-500">No vehicle record</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="bg-gray-50 rounded-xl p-3 grid grid-cols-3 gap-2 text-center">
        <div>
          <p className="text-lg font-bold text-gray-900">{stats?.total ?? accidents.length}</p>
          <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">Accidents</p>
        </div>
        <div>
          <p className={`text-lg font-bold ${openCount > 0 ? 'text-red-600' : 'text-gray-900'}`}>
            {openCount}
          </p>
          <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">Open</p>
        </div>
        <div>
          <p className="text-lg font-bold text-gray-900">${totalClaimed.toFixed(2)}</p>
          <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">Claimed</p>
        </div>
      </div>

      <div className="bg-gray-50 rounded-xl p-3">
        <h4 className="flex items-center gap-2 text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
          <Siren className="w-3.5 h-3.5" />
          Accident Records
          {accidents.length > 0 && (
            <span className="ml-1 normal-case text-gray-400">({accidents.length})</span>
          )}
        </h4>

        {isLoading ? (
          <p className="text-sm text-gray-400 py-2">Loading accident history…</p>
        ) : isError ? (
          <p className="text-sm text-red-500 py-2">Could not load accident history.</p>
        ) : accidents.length === 0 ? (
          <p className="text-sm text-gray-400 py-2">No accidents recorded for this vehicle.</p>
        ) : (
          <ul className="space-y-2">
            {paged.map((a) => (
              <li key={a.id}>
                <button
                  type="button"
                  onClick={() => setDetailAccident(a)}
                  className="w-full text-left bg-white rounded-lg border border-gray-200 px-3 py-2 hover:border-gray-300 hover:shadow-sm transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-1"
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-sm font-medium text-gray-900 whitespace-nowrap">
                        {formatDate(a.accident_datetime)}
                      </span>
                      <StatusBadge
                        status={accidentBadgeStatusFor(a.status)}
                        label={a.status_display}
                      />
                    </div>
                    {a.claim_status && a.claim_status !== 'not_started' && (
                      <StatusBadge
                        status={claimBadgeStatusFor(a.claim_status)}
                        label={a.claim_status_display}
                      />
                    )}
                  </div>
                  <div className="mt-1 flex items-center gap-1.5 text-xs text-gray-600">
                    <FileText className="w-3 h-3 flex-shrink-0 text-gray-400" />
                    <span className="truncate">{a.accident_type_display}</span>
                    <span className="text-gray-300">·</span>
                    <span className="text-gray-400 whitespace-nowrap">{a.reference}</span>
                  </div>
                  <div className="mt-0.5 flex items-center gap-1.5 text-xs text-gray-600">
                    <User className="w-3 h-3 flex-shrink-0 text-gray-400" />
                    <span className="truncate">{a.driver_display_name || 'Driver not identified'}</span>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setPage}
        totalCount={accidents.length}
        pageSize={ACCIDENT_HISTORY_PAGE_SIZE}
        theme="admin"
        noun="accidents"
      />

      <AccidentDetailModal
        isOpen={!!detailAccident}
        onClose={() => setDetailAccident(null)}
        accident={detailAccident}
        readOnly
      />
    </div>
  );
};

/**
 * Dedicated "Traffic Offences" tab for fleet vehicles — every notice ever
 * received against THIS plate, newest first. Answers the questions that only
 * make sense per-vehicle:
 *   - Does this vehicle keep attracting the same offence?
 *   - Which drivers were declared, and did any of them dispute?
 *   - Is anything still open against it before we transfer or dispose it?
 *
 * Deadline-driven work lives on /asset-admin/fleet-offences instead — you can't
 * find what's due this week by opening vehicles one at a time. This tab is the
 * per-asset record; that page is the queue.
 */
const FleetOffenceHistoryTab = ({ fleet }) => {
  const [page, setPage] = useState(1);
  const [detailNoticeId, setDetailNoticeId] = useState(null);
  const fleetVehicleId = fleet?.id;

  const { data: notices = [], isLoading, isError } = useTrafficOffences(
    fleetVehicleId
      ? { fleet_vehicle: fleetVehicleId, include_closed: 'true', page_size: 200 }
      : {},
    { enabled: !!fleetVehicleId }
  );

  const openCount = useMemo(
    () => notices.filter((n) => !['closed', 'cancelled'].includes(n.status)).length,
    [notices]
  );
  const totalFines = useMemo(
    () => notices
      .filter((n) => n.status !== 'cancelled')
      .reduce((sum, n) => sum + Number(n.fine_amount || 0), 0),
    [notices]
  );

  const totalPages = Math.max(1, Math.ceil(notices.length / OFFENCE_HISTORY_PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const paged = notices.slice(
    (currentPage - 1) * OFFENCE_HISTORY_PAGE_SIZE,
    currentPage * OFFENCE_HISTORY_PAGE_SIZE
  );

  if (!fleet) {
    return (
      <div className="text-center py-8">
        <ShieldAlert className="w-12 h-12 text-gray-300 mx-auto mb-3" />
        <p className="text-gray-500">No vehicle record</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Glanceable summary — mirrors the Inspection History stats strip. */}
      <div className="bg-gray-50 rounded-xl p-3 grid grid-cols-3 gap-2 text-center">
        <div>
          <p className="text-lg font-bold text-gray-900">{notices.length}</p>
          <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">Notices</p>
        </div>
        <div>
          <p className={`text-lg font-bold ${openCount > 0 ? 'text-red-600' : 'text-gray-900'}`}>
            {openCount}
          </p>
          <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">Open</p>
        </div>
        <div>
          <p className="text-lg font-bold text-gray-900">${totalFines.toFixed(2)}</p>
          <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">Total Fines</p>
        </div>
      </div>

      <div className="bg-gray-50 rounded-xl p-3">
        <h4 className="flex items-center gap-2 text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
          <ShieldAlert className="w-3.5 h-3.5" />
          Offence Notices
          {notices.length > 0 && (
            <span className="ml-1 normal-case text-gray-400">({notices.length})</span>
          )}
        </h4>

        {isLoading ? (
          <p className="text-sm text-gray-400 py-2">Loading offence history…</p>
        ) : isError ? (
          <p className="text-sm text-red-500 py-2">Could not load offence history.</p>
        ) : notices.length === 0 ? (
          <p className="text-sm text-gray-400 py-2">No traffic offences recorded for this vehicle.</p>
        ) : (
          <ul className="space-y-2">
            {paged.map((n) => (
              <li key={n.id}>
                <button
                  type="button"
                  onClick={() => setDetailNoticeId(n.id)}
                  className="w-full text-left bg-white rounded-lg border border-gray-200 px-3 py-2 hover:border-gray-300 hover:shadow-sm transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-1"
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-sm font-medium text-gray-900 whitespace-nowrap">
                        {formatDate(n.offence_datetime)}
                      </span>
                      <StatusBadge
                        status={offenceBadgeStatus(n.status)}
                        label={n.status_display}
                      />
                    </div>
                    <span className="text-xs text-gray-500 whitespace-nowrap tabular-nums">
                      ${Number(n.fine_amount || 0).toFixed(2)}
                    </span>
                  </div>
                  {n.ops_status && (
                    <p className="mt-0.5 text-[11px] text-gray-400">
                      Ops: {String(n.ops_status).replace(/_/g, ' ')}
                    </p>
                  )}
                  <div className="mt-1 flex items-center gap-1.5 text-xs text-gray-600">
                    <FileText className="w-3 h-3 flex-shrink-0 text-gray-400" />
                    <span className="truncate">{n.offence_type_display}</span>
                    <span className="text-gray-300">·</span>
                    <span className="text-gray-400 whitespace-nowrap">{n.notice_number}</span>
                  </div>
                  <div className="mt-0.5 flex items-center gap-1.5 text-xs text-gray-600">
                    <User className="w-3 h-3 flex-shrink-0 text-gray-400" />
                    <span className="truncate">
                      {n.is_company_offence
                        ? 'Company offence'
                        : n.current_driver_name || 'Driver not identified'}
                    </span>
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setPage}
        totalCount={notices.length}
        pageSize={OFFENCE_HISTORY_PAGE_SIZE}
        theme="admin"
        noun="notices"
      />

      <TrafficOffenceDetailModal
        isOpen={!!detailNoticeId}
        onClose={() => setDetailNoticeId(null)}
        noticeId={detailNoticeId}
      />
    </div>
  );
};

// ============================================
// FLEET VEHICLE SECTION
// ============================================

const FleetIncidentCounters = ({ fleet }) => {
  const fleetVehicleId = fleet?.id;
  const { data: accidentStats } = useAccidentVehicleStats(fleetVehicleId);
  const { data: notices = [] } = useTrafficOffences(
    fleetVehicleId
      ? { fleet_vehicle: fleetVehicleId, include_closed: 'true', page_size: 200 }
      : {},
    { enabled: !!fleetVehicleId }
  );
  const openOffences = notices.filter((n) => !['closed', 'cancelled'].includes(n.status)).length;

  return (
    <div className="bg-gray-50 rounded-xl p-3 grid grid-cols-2 gap-2 text-center">
      <div>
        <p className={`text-lg font-bold ${(accidentStats?.open || 0) > 0 ? 'text-red-600' : 'text-gray-900'}`}>
          {accidentStats?.total ?? 0}
          {(accidentStats?.open || 0) > 0 && (
            <span className="text-xs font-medium text-red-600 ml-1">
              ({accidentStats.open} open)
            </span>
          )}
        </p>
        <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">Accidents</p>
      </div>
      <div>
        <p className={`text-lg font-bold ${openOffences > 0 ? 'text-red-600' : 'text-gray-900'}`}>
          {notices.length}
          {openOffences > 0 && (
            <span className="text-xs font-medium text-red-600 ml-1">
              ({openOffences} open)
            </span>
          )}
        </p>
        <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">Offences</p>
      </div>
    </div>
  );
};

const FleetVehicleSection = ({
  fleet: initialFleet,
  asset,
  isDisposed = false,
  isEmployeeView = false,
  canSyncOps = false,
}) => {
  const syncMutation = useSyncFleetVehicleCompliance();
  const [syncedFleet, setSyncedFleet] = useState(null);

  useEffect(() => {
    setSyncedFleet(null);
  }, [initialFleet?.id]);

  if (!initialFleet) return null;

  const fleet = syncedFleet || initialFleet;
  const opsSyncedAt = fleet.ops_compliance_synced_at;
  const opsSyncTimestamp = opsSyncedAt ? new Date(opsSyncedAt) : null;
  const opsSyncIsStale = (
    !opsSyncTimestamp
    || Number.isNaN(opsSyncTimestamp.getTime())
    || Date.now() - opsSyncTimestamp.getTime() > 36 * 60 * 60 * 1000
  );
  const opsSyncLabel = opsSyncedAt
    ? `Last checked ${formatRelativeTime(opsSyncedAt)}`
    : 'Not yet synced';
  const opsSyncTitle = opsSyncedAt
    ? `Last checked ${formatDateTimeCompact(opsSyncedAt)}`
    : 'No successful Ops Portal compliance sync has been recorded.';

  // Leased / rented vehicles do not have an LTA log card available to the
  // company, so the chassis / engine / OMV / ARF / PARF / COE-category /
  // weights / scheme fields are all unknowable. We hide those sections and
  // show a banner instead, rather than rendering rows of em-dashes.
  const isLeased =
    fleet.ownership_type === 'leased' || fleet.ownership_type === 'rented';

  return (
    <div className="space-y-3">
      {!isEmployeeView && <FleetIncidentCounters fleet={fleet} />}

      {/* Disposal Information - Show at top if disposed */}
      {isDisposed && (asset?.disposal_date || asset?.disposal_amount || asset?.disposal_method) && (
        <Section title="Disposal Information" icon={AlertCircle}>
          <DetailField label="Disposal Date" value={formatDate(asset.disposal_date)} />
          <DetailField label="Method" value={asset.disposal_method} />
          <DetailField label="Amount Received" value={asset.disposal_amount ? formatCurrency(asset.disposal_amount) : '—'} />
          <DetailField label="Buyer / Agent" value={asset.disposal_buyer || '—'} />
          {asset.disposal_remarks && (
            <div className="sm:col-span-2">
              <dt className="text-xs text-gray-500 font-medium uppercase tracking-wide">Remarks</dt>
              <dd className="mt-1 text-sm text-gray-700">{asset.disposal_remarks}</dd>
            </div>
          )}
        </Section>
      )}

      {/* Leased / rented banner — explains why LTA log card data is missing */}
      {isLeased && (
        <div className="rounded-md border border-blue-200 bg-blue-50 px-3 py-2 flex items-start gap-2">
          <Building2 className="w-4 h-4 mt-0.5 text-blue-600 flex-shrink-0" />
          <div className="text-xs text-blue-900 leading-relaxed">
            <span className="font-semibold">Leased vehicle.</span>{' '}
            Log card details (chassis, engine, OMV, ARF, COE category, weights)
            are maintained by the lessor and not tracked here. See the{' '}
            <span className="font-medium">Lease</span> section below for
            ownership and cost details.
          </div>
        </div>
      )}

      {/* Most-used fleet facts: identity, dates and COE position. */}
      {/* For leased vehicles, the LTA Type / Manufacturing Year / COE Category come */}
      {/* from the log card and are unknown to us, so we hide those rows. */}
      <Section title="Vehicle Snapshot" icon={Truck}>
        <DetailField label="Vehicle Number" value={fleet.vehicle_number} />
        <DetailField label="Vehicle Type" value={fleet.vehicle_type_display} />
        {!isLeased && (
          <DetailField label="LTA Type" value={[
            fleet.lta_vehicle_type_code,
            fleet.lta_vehicle_type_label,
          ].filter(Boolean).join(' — ')} className="sm:col-span-2" />
        )}
        <DetailField label="Registration Date" value={formatDate(fleet.registration_date)} />
        {!isLeased && (
          <DetailField label="Manufacturing Year" value={fleet.manufacturing_year} />
        )}
        {!isLeased && (
          <DetailField label="COE Category" value={fleet.coe_category_display || fleet.coe_category || '—'} />
        )}
        <DetailFieldWithBadge
          label="COE Expiry"
          value={formatDate(fleet.coe_expiry_date)}
          badge={getExpiryBadge(fleet.coe_expiry_date, isDisposed)}
        />
      </Section>

      {/* Operational renewal dates that usually require action. */}
      <Section title="Regulatory Renewals" icon={Shield}>
        <DetailFieldWithBadge
          label="Road Tax Expiry"
          value={formatDate(fleet.road_tax_expiry_date)}
          badge={getExpiryBadge(fleet.road_tax_expiry_date, isDisposed)}
        />
        <DetailFieldWithBadge
          label="Inspection Valid Till"
          value={formatDate(fleet.inspection_expiry_date)}
          badge={getExpiryBadge(fleet.inspection_expiry_date, isDisposed)}
        />
        <DetailField label="IU Number" value={fleet.iu_number} />
        {!isEmployeeView && (
          <div
            className={`sm:col-span-2 mt-1 rounded-lg border px-3 py-2 flex items-center justify-between gap-3 ${
              opsSyncIsStale
                ? 'border-amber-200 bg-amber-50'
                : 'border-blue-200 bg-blue-50'
            }`}
          >
            <div className="min-w-0 flex items-start gap-2">
              {opsSyncIsStale ? (
                <AlertTriangle className="w-4 h-4 mt-0.5 text-amber-600 flex-shrink-0" />
              ) : (
                <RefreshCw className="w-4 h-4 mt-0.5 text-blue-600 flex-shrink-0" />
              )}
              <div className="min-w-0">
                <p className={`text-xs font-semibold ${
                  opsSyncIsStale ? 'text-amber-900' : 'text-blue-900'
                }`}>
                  Sourced from Ops Portal
                </p>
                <p
                  className={`text-xs ${
                    opsSyncIsStale ? 'text-amber-700' : 'text-blue-700'
                  }`}
                  title={opsSyncTitle}
                >
                  {opsSyncLabel}
                  {opsSyncIsStale && ' · values may be stale'}
                </p>
              </div>
            </div>
            {canSyncOps && (
              <button
                type="button"
                onClick={() => syncMutation.mutate(
                  { fleetVehicleId: fleet.id },
                  {
                    onSuccess: (data) => {
                      if (data?.fleet_vehicle) setSyncedFleet(data.fleet_vehicle);
                    },
                  }
                )}
                disabled={syncMutation.isPending}
                className="icon-button-admin flex-shrink-0"
                title="Sync compliance dates from Ops Portal"
                aria-label="Sync compliance dates from Ops Portal"
              >
                <RefreshCw className={`w-4 h-4 ${
                  syncMutation.isPending ? 'animate-spin' : ''
                }`} />
              </button>
            )}
          </div>
        )}
      </Section>

      {/* NOTE: the odometer snapshot + full workshop service history now live in
          the dedicated "Maintenance History" tab (see FleetMaintenanceTab), so the
          Overview stays focused on identity / compliance / financials. */}

      {/* Static LTA identity and specs used for verification, insurance and sale/scrap workflows. */}
      {/* Hidden for leased/rented vehicles — log card data is owned by the lessor. */}
      {!isLeased && (
        <Section title="LTA Identity & Specs" icon={FileText}>
          <DetailField label="Chassis No." value={fleet.chassis_number || asset.serial_number} className="sm:col-span-2" />
          <DetailField label="Engine No." value={fleet.engine_number} className="sm:col-span-2" />
          <DetailField label="Engine Capacity" value={fleet.engine_capacity_cc ? `${formatInteger(fleet.engine_capacity_cc)} cc` : '—'} />
          <DetailField label="Power Output" value={formatEngineOutput(fleet)} />
          <DetailField label="Colour" value={fleet.color} />
          <DetailField label="Cabin Passenger Capacity" value={fleet.passenger_capacity} />
          <DetailField label="Maximum Passenger Capacity" value={fleet.maximum_passenger_capacity} />
          <DetailField label="Unladen Weight" value={fleet.unladen_weight_kg ? `${formatInteger(fleet.unladen_weight_kg)} kg` : '—'} />
          <DetailField label="Max Laden Weight" value={fleet.maximum_laden_weight_kg ? `${formatInteger(fleet.maximum_laden_weight_kg)} kg` : '—'} />
        </Section>
      )}

      {/* Money fields are grouped below identity/compliance because they are reference data. */}
      {/* Hidden for leased/rented vehicles — they pay a monthly lease, not OMV/ARF/PARF/etc. */}
      {/* Hidden in employee view — purchase economics are finance/admin data, like the */}
      {/* "Purchase Information" section we already hide for IT/default assets. */}
      {!isLeased && !isEmployeeView && (
        <Section title="LTA Financials" icon={CreditCard}>
          <DetailField label="OMV" value={formatCurrency(fleet.open_market_value)} />
          <DetailField label="COE Price" value={formatCurrency(fleet.quota_premium)} />
          <DetailField label="ARF Paid" value={formatCurrency(fleet.arf_paid)} />
          <DetailField label="Minimum PARF Benefit" value={formatCurrency(fleet.min_parf_benefit)} />
          <DetailField label="PQP Paid" value={formatCurrency(fleet.pqp_paid)} />
          <DetailField label="CEVS / VES Rebate" value={formatCurrency(fleet.cevs_ves_rebate)} />
        </Section>
      )}

      {!isLeased && (fleet.under_early_turnover_scheme || fleet.requires_side_marking || fleet.body_attachments) && (
        <Section title="Schemes & Body" icon={Settings}>
          <DetailField label="Early Turnover Scheme" value={fleet.under_early_turnover_scheme ? 'Yes' : 'No'} />
          <DetailField label="Side Marking Required" value={fleet.requires_side_marking ? 'Yes' : 'No'} />
          <DetailField label="Body Attachments" value={fleet.body_attachments} className="sm:col-span-2" />
        </Section>
      )}

      {/* Insurance remains below LTA facts; it changes over time and is vendor/policy-specific. */}
      <Section title="Insurance" icon={Shield}>
        <DetailFieldWithBadge
          label="Expiry Date"
          value={formatDate(fleet.insurance_expiry_date)}
          badge={getExpiryBadge(fleet.insurance_expiry_date, isDisposed)}
        />
        <DetailField label="Start Date" value={formatDate(fleet.insurance_start_date)} />
        <DetailField label="Certificate No." value={fleet.insurance_certificate} />
        {/* Premium is a finance figure — hidden from the employee/driver view. */}
        {!isEmployeeView && (
          <DetailField label="Premium" value={formatCurrency(fleet.insurance_premium)} />
        )}
        
        {/* Agency - from insurance_intermediary FK */}
        <div className="sm:col-span-2">
          <dt className="text-xs text-gray-500 font-medium uppercase tracking-wide">Agency</dt>
          <dd className="mt-1">
            {fleet.insurance_intermediary?.company_name ? (
              <>
                <span className="text-sm text-gray-900">{fleet.insurance_intermediary.company_name}</span>
                {fleet.insurance_intermediary.uen && (
                  <span className="ml-2 px-2 py-0.5 bg-gray-100 text-gray-500 rounded text-xs font-mono">
                    {fleet.insurance_intermediary.uen}
                  </span>
                )}
              </>
            ) : (
              <span className="text-sm text-gray-400">—</span>
            )}
          </dd>
        </div>
        
        {/* Insurer - from insurance_insurer FK */}
        <div className="sm:col-span-2">
          <dt className="text-xs text-gray-500 font-medium uppercase tracking-wide">Insurer</dt>
          <dd className="mt-1">
            {fleet.insurance_insurer?.company_name ? (
              <>
                <span className="text-sm text-gray-900">{fleet.insurance_insurer.company_name}</span>
                {fleet.insurance_insurer.uen && (
                  <span className="ml-2 px-2 py-0.5 bg-gray-100 text-gray-500 rounded text-xs font-mono">
                    {fleet.insurance_insurer.uen}
                  </span>
                )}
              </>
            ) : (
              <span className="text-sm text-gray-400">—</span>
            )}
          </dd>
        </div>
      </Section>

      {/* Lease (for leased/rented vehicles). Hidden in employee view — monthly */}
      {/* lease cost and lessor are commercial/contract data, not driver info. */}
      {!isEmployeeView && (fleet.ownership_type === 'leased' || fleet.ownership_type === 'rented') && (
        <Section title="Lease" icon={FileText}>
          <DetailField label="Ownership" value={fleet.ownership_type_display || fleet.ownership_type} />
          <DetailField label="Monthly Lease" value={formatCurrency(fleet.monthly_lease_cost)} />
          <div className="sm:col-span-2">
            <dt className="text-xs text-gray-500 font-medium uppercase tracking-wide">Lessor</dt>
            <dd className="mt-1">
              {fleet.lessor?.company_name ? (
                <>
                  <span className="text-sm text-gray-900">{fleet.lessor.company_name}</span>
                  {fleet.lessor.uen && (
                    <span className="ml-2 px-2 py-0.5 bg-gray-100 text-gray-500 rounded text-xs font-mono">
                      {fleet.lessor.uen}
                    </span>
                  )}
                </>
              ) : (
                <span className="text-sm text-gray-400">—</span>
              )}
            </dd>
          </div>
        </Section>
      )}
    </div>
  );
};

// ============================================
// SOFTWARE LICENSE SECTION
// ============================================

const SoftwareLicenseSection = ({ license, seatAssignments, isDisposed = false }) => {
  if (!license) return null;
  
  return (
    <div className="space-y-3">
      {/* License Details */}
      <Section title="License Details" icon={FileKey}>
        <DetailField label="Product" value={license.product_name} />
        <DetailField label="Publisher" value={license.publisher} />
        <DetailField label="Version" value={license.version} />
        <DetailField label="License Type" value={license.license_type_display} />
        <DetailField label="Subscription ID" value={license.subscription_id} />
        <DetailField label="Contract Ref" value={license.contract_reference} />
      </Section>
      
      {/* Subscription */}
      <Section title="Subscription" icon={Calendar}>
        <DetailField label="Start Date" value={formatDate(license.subscription_start_date)} />
        <DetailFieldWithBadge 
          label="End Date" 
          value={formatDate(license.subscription_end_date)}
          badge={getExpiryBadge(license.subscription_end_date, isDisposed)}
        />
        <DetailField 
          label="Auto Renewal" 
          value={license.auto_renewal ? 'Yes' : 'No'} 
        />
      </Section>
      
      {/* Seat Management */}
      <Section title="Seat Management" icon={Users}>
        <DetailField label="Total Seats" value={license.total_seats} />
        <DetailField label="Assigned" value={license.assigned_seat_count} />
        <DetailField label="Available" value={license.available_seats} />
      </Section>
      
      {/* Assigned Users */}
      {seatAssignments && seatAssignments.length > 0 && (
        <div className="bg-gray-50 rounded-xl p-4">
          <h4 className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-3">
            <User className="w-4 h-4" />
            Assigned Users ({seatAssignments.length})
          </h4>
          <div className="space-y-2">
            {seatAssignments.map((seat, idx) => (
              <div key={idx} className="flex items-center justify-between py-2 border-b border-gray-200 last:border-0">
                <div>
                  <p className="text-sm font-medium text-gray-900">{seat.employee_name}</p>
                  <p className="text-xs text-gray-500">{seat.employee_staff_id}</p>
                </div>
                <span className="text-xs text-gray-500">Since {formatDate(seat.assigned_date)}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================
// IT EQUIPMENT SECTION
// ============================================

const EndpointProtectionSection = ({ asset }) => {
  // `trendmicro` is the normalized status from the serializer; fall back to raw
  // custom_fields for resilience. Render nothing when never synced.
  const tm = asset.trendmicro || (
    asset.custom_fields && 'trendmicro_installed' in asset.custom_fields
      ? {
          installed: !!asset.custom_fields.trendmicro_installed,
          install_date: asset.custom_fields.trendmicro_install_date,
          endpoint: asset.custom_fields.trendmicro_endpoint,
          synced_at: asset.custom_fields.trendmicro_synced_at,
        }
      : null
  );
  if (!tm) return null;

  const installed = !!tm.installed;

  return (
    <Section title="Endpoint Protection" icon={installed ? ShieldCheck : ShieldOff}>
      <div className="sm:col-span-2">
        <dt className="text-xs text-gray-500 font-medium uppercase tracking-wide">Trend Micro Apex One</dt>
        <dd className="mt-1">
          {installed ? (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
              <ShieldCheck className="w-3.5 h-3.5" />
              Agent installed
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
              <ShieldOff className="w-3.5 h-3.5" />
              No agent detected
            </span>
          )}
        </dd>
      </div>
      {installed && (
        <DetailField label="Installed On" value={formatDate(tm.install_date)} />
      )}
      {tm.synced_at && (
        <DetailField label="Last Synced" value={formatDate(tm.synced_at)} />
      )}
      {tm.endpoint && tm.endpoint !== asset.asset_tag && (
        <DetailField label="Endpoint Name" value={tm.endpoint} className="sm:col-span-2" />
      )}
    </Section>
  );
};

const ITEquipmentSection = ({ asset, isDisposed = false, isEmployeeView = false }) => {
  // Trend Micro keys live in their own section; keep them out of the generic specs list.
  const specEntries = Object.entries(asset.custom_fields || {}).filter(
    ([key]) => !TRENDMICRO_CUSTOM_FIELD_KEYS.includes(key)
  );
  const hasCustomFields = specEntries.length > 0;
  
  return (
    <div className="space-y-3">
      {/* Device Details */}
      <Section title="Device Details" icon={Laptop}>
        <DetailField label="Serial Number" value={asset.serial_number} />
        <DetailField label="Brand" value={asset.brand} />
        <DetailField label="Model" value={asset.model} />
        <DetailField label="Condition" value={asset.condition_display} />
      </Section>
      
      {/* Location (project + work site from Tender API) */}
      {!isEmployeeView && (asset.deployed_project_name || asset.location) && (
        <Section title="Location" icon={MapPin}>
          {asset.deployed_project_name && (
            <>
              <DetailField label="Project" value={`${asset.deployed_project_code} — ${asset.deployed_project_name}`} className="sm:col-span-2" />
              {asset.deployed_work_site_name && (
                <DetailField label="Work Site" value={asset.deployed_work_site_name} />
              )}
              {asset.deployed_date && (
                <DetailField label="Since" value={formatDate(asset.deployed_date)} />
              )}
            </>
          )}
          {asset.location && (
            <DetailField label="Notes" value={asset.location} className="sm:col-span-2" />
          )}
        </Section>
      )}

      {/* Endpoint Protection (Trend Micro Apex One) */}
      <EndpointProtectionSection asset={asset} />
      
      {/* Specifications (from custom_fields) */}
      {hasCustomFields && (
        <Section title="Specifications" icon={Settings}>
          {specEntries.map(([key, value]) => (
            <DetailField key={key} label={formatCustomFieldLabel(key)} value={value} />
          ))}
        </Section>
      )}
      
      {/* Warranty - Simplified for employees */}
      <Section title="Warranty" icon={Shield}>
        {!isEmployeeView && (
          <DetailField label="Start Date" value={formatDate(asset.warranty_start_date)} />
        )}
        <DetailFieldWithBadge 
          label={isEmployeeView ? "Warranty Until" : "End Date"}
          value={formatDate(asset.warranty_end_date)}
          badge={asset.warranty_end_date && getExpiryBadge(asset.warranty_end_date, isDisposed, { variant: 'warranty' })}
        />
        {!isEmployeeView && (
          <DetailField label="Provider" value={asset.warranty_provider} />
        )}
        <DetailField 
          label="Status" 
          value={asset.is_under_warranty ? 'Under Warranty' : 'Expired'} 
        />
      </Section>
      
      {/* Purchase Info - Admin only */}
      {!isEmployeeView && (
        <Section title="Purchase Information" icon={CreditCard}>
        <DetailField
          label="Ownership"
          value={asset.ownership_type_display || asset.ownership_type || '—'}
        />
        {asset.lease_return_date && (
          <DetailField label="Returned" value={formatDate(asset.lease_return_date)} />
        )}
        {asset.lease_returned_to && (
          <DetailField label="Returned to" value={asset.lease_returned_to} />
        )}
        <DetailField label="Date" value={formatDate(asset.purchase_date)} />
          <DetailField label="Price" value={formatCurrency(asset.purchase_price)} />
          <DetailField label="Vendor" value={
            asset.vendor_detail ? (
              <span className="inline-flex items-center gap-1.5">
                {asset.vendor_detail.name}
                {asset.vendor_detail.uen && (
                  <span className={`inline-flex items-center gap-0.5 text-xs px-1.5 py-0.5 rounded-full ${
                    asset.vendor_detail.acra_verified
                      ? 'bg-green-100 text-green-700'
                      : 'bg-gray-100 text-gray-600'
                  }`}>
                    {asset.vendor_detail.acra_verified && (
                      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    )}
                    UEN: {asset.vendor_detail.uen}
                  </span>
                )}
              </span>
            ) : asset.vendor_name || asset.purchase_vendor
          } />
          <DetailField label="PO Number" value={asset.purchase_order_number} />
          <DetailField label="Invoice" value={asset.invoice_number} />
          <DetailField label="Age" value={formatAgeYearsMonths(asset.purchase_date)} />
        </Section>
      )}
    </div>
  );
};

// ============================================
// ACCESS CONTROL SECTION
// ============================================

const AccessControlSection = ({ asset }) => {
  // Access is "Deactivated" only when it was explicitly deactivated — either the
  // status is set to 'deactivated' or a deactivation_date is recorded. An ACC
  // card that is merely 'assigned'/'active' (and never revoked) is still active,
  // so we must not treat every non-'active' status as deactivated.
  const isDeactivated = asset.status === 'deactivated' || !!asset.deactivation_date;

  return (
    <div className="space-y-3">
      <Section title="Access Details" icon={Key}>
        <DetailField label="Asset Tag" value={asset.asset_tag} />
        <DetailField
          label="Status"
          value={isDeactivated ? 'Deactivated' : 'Active'}
        />
        <DetailField label="Activation Date" value={formatDate(asset.activation_date)} />
        <DetailField label="Deactivation Date" value={formatDate(asset.deactivation_date)} />
        {isDeactivated && (
          <DetailField
            label="Deactivated By"
            value={asset.deactivated_by_name
              ? `${asset.deactivated_by_name}${asset.deactivated_by_staff_id ? ` (${asset.deactivated_by_staff_id})` : ''}`
              : 'Automated (employment ended)'}
          />
        )}
      </Section>
    </div>
  );
};

const formatLineNumber = (value) => {
  const digits = String(value || '').replace(/\D/g, '');
  if (digits.length === 8) return `${digits.slice(0, 4)} ${digits.slice(4)}`;
  return value || '—';
};

const formatMonthlyFee = (amount) => {
  if (amount === null || amount === undefined || amount === '') return '—';
  const numeric = Number(amount);
  if (Number.isNaN(numeric)) return '—';
  return `$${numeric.toLocaleString('en-SG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} / month`;
};

// ============================================
// MOBILE LINE SECTION
// ============================================

const MobileLineSection = ({ line, asset, isDisposed = false, isEmployeeView = false }) => {
  if (!line) return null;

  const needsHolder = !isEmployeeView
    && !asset?.current_assignee_staff_id
    && asset?.status === 'deployed';
  const holderIsEquipment = line.holder_kind === 'equipment';

  return (
    <div className="space-y-3">
      <Section title="Line" icon={Smartphone}>
        <DetailField label="Number" value={formatLineNumber(line.phone_number)} />
        <DetailField label="Telco" value={line.telco_display} />
        {holderIsEquipment && line.holder_label && (
          <DetailField
            label="Deployment context"
            value={line.holder_label}
            className="sm:col-span-2"
          />
        )}
        <DetailField label="Plan" value={line.plan_name} className="sm:col-span-2" />
        <DetailField label="Data" value={line.bundled_data} />
        <DetailField label="Talktime" value={line.bundled_talktime} />
        <DetailField label="SIM" value={line.sim_form} />
        <DetailField label="ICCID" value={asset?.serial_number} />
        {!isEmployeeView && (
          <DetailField label="Monthly fee" value={formatMonthlyFee(line.monthly_fee)} />
        )}
        {line.handset_note && (
          <DetailField label="Handset note" value={line.handset_note} className="sm:col-span-2" />
        )}
      </Section>

      <Section title="Lock-in" icon={Calendar}>
        <DetailField label="Term" value={line.contract_term || 'Not recorded'} />
        <DetailField label="Starts" value={formatDate(line.contract_start_date)} />
        <DetailFieldWithBadge
          label="Lock-in ends"
          value={line.contract_end_date ? formatDate(line.contract_end_date) : 'No date on record'}
          badge={line.contract_end_date
            ? getExpiryBadge(line.contract_end_date, isDisposed, { variant: 'contract' })
            : null}
        />
        <DetailField label="Issued" value={formatDate(line.issued_on)} />
      </Section>

      {holderIsEquipment && asset?.current_assignee_staff_id && (
        <div className="flex items-start gap-2 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2">
          <Smartphone className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-blue-800">
            {isEmployeeView
              ? `You are accountable for this line. The SIM stays in ${line.holder_label || 'a device'}. Your Work Phone is left unchanged.`
              : `${asset.current_assignee_name} (${asset.current_assignee_staff_id}) is accountable for this line. The SIM stays in ${line.holder_label || 'a device'}. Their Work Phone is left unchanged.`}
          </p>
        </div>
      )}

      {needsHolder && (
        <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
          <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-amber-800">
            {holderIsEquipment
              ? `This device line still needs a staff member who is accountable for it${line.holder_label ? ` at ${line.holder_label}` : ''}.`
              : `The register names ${line.holder_label || 'a holder'}, but that is not linked to a staff record. Confirm who has the SIM before assigning it.`}
          </p>
        </div>
      )}

      {!isEmployeeView && asset?.remarks && (
        <Section title="Register notes" icon={FileText}>
          <div className="sm:col-span-2">
            <p className="text-sm text-gray-700 whitespace-pre-wrap">{asset.remarks}</p>
          </div>
        </Section>
      )}
    </div>
  );
};

// ============================================
// FUEL CARD SECTION
// ============================================

const FuelCardSection = ({ fuelCard, asset, isDisposed = false, onNavigateToAsset }) => {
  if (!fuelCard) return null;

  // Work-use vehicle: a company link drills into the fleet asset; a personal
  // car shows the declared plate + ownership badge; nothing recorded flags the
  // audit gap explicitly rather than rendering a silent dash.
  const resolvedPlate = fuelCard.work_vehicle_plate || fuelCard.linked_vehicle_number || fuelCard.work_use_plate;
  const isCompanyVehicle = !!fuelCard.linked_vehicle_asset_id;
  const canNavigateToVehicle = !!(isCompanyVehicle && onNavigateToAsset);
  const companyVehicleLabel = `${fuelCard.linked_vehicle_brand || ''} ${fuelCard.linked_vehicle_model || ''}`.trim() || '—';
  const ownershipLabel = fuelCard.vehicle_ownership_display
    || (isCompanyVehicle ? 'Company Vehicle' : null);

  return (
    <div className="space-y-3">
      {/* Card Details */}
      <Section title="Card Details" icon={CreditCard}>
        <DetailField label="Card ID" value={asset?.asset_tag} />
        <DetailField label="Account No." value={fuelCard.primary_card_number} />
        <DetailField label="Card No." value={fuelCard.secondary_card_number} />
        <ToggleablePINField label="PIN" pin={fuelCard.pin} />
        <DetailField label="Fuel Type" value={fuelCard.fuel_type_display} />
        <DetailField label="Monthly Limit" value={fuelCard.monthly_limit ? formatCurrency(fuelCard.monthly_limit) : 'No Limit'} />
      </Section>

      {/* Work-Use Vehicle (audit justification) */}
      <Section title="Work-Use Vehicle" icon={Car}>
        {resolvedPlate ? (
          canNavigateToVehicle ? (
            <div className="sm:col-span-2">
              <button
                type="button"
                onClick={() => onNavigateToAsset(fuelCard.linked_vehicle_asset_id)}
                title={`Open vehicle ${fuelCard.linked_vehicle_asset_tag || resolvedPlate}`}
                aria-label={`Open vehicle ${resolvedPlate}`}
                className="group w-full -mx-2 px-2 py-2 rounded-lg border border-transparent bg-transparent hover:bg-white hover:border-gray-200 transition-colors text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2"
              >
                <div className="flex items-center gap-3">
                  <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
                    <div>
                      <span className="block text-xs text-gray-500 font-medium">Plate</span>
                      <span className="mt-0.5 block text-sm text-gray-900 group-hover:text-red-700 transition-colors">
                        {resolvedPlate}
                      </span>
                    </div>
                    <div>
                      <span className="block text-xs text-gray-500 font-medium">Vehicle</span>
                      <span className="mt-0.5 block text-sm text-gray-900">{companyVehicleLabel}</span>
                    </div>
                  </div>
                  <ExternalLink className="w-4 h-4 text-gray-400 group-hover:text-red-600 transition-colors flex-shrink-0" />
                </div>
              </button>
            </div>
          ) : (
            <>
              <DetailField label="Plate" value={resolvedPlate} />
              <DetailField label="Ownership" value={ownershipLabel || '—'} />
              {(isCompanyVehicle ? companyVehicleLabel !== '—' : !!fuelCard.work_use_vehicle_desc) && (
                <DetailField
                  label="Vehicle"
                  value={isCompanyVehicle ? companyVehicleLabel : fuelCard.work_use_vehicle_desc}
                />
              )}
              {fuelCard.work_use_effective_date && (
                <DetailField label="Effective Date" value={formatDate(fuelCard.work_use_effective_date)} />
              )}
            </>
          )
        ) : (
          <div className="sm:col-span-2 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
            <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-amber-700">
              No work-use vehicle recorded — this card has no documented audit justification.
            </p>
          </div>
        )}
      </Section>

      {/* Validity & Dates */}
      <Section title="Validity" icon={Calendar}>
        <DetailFieldWithBadge
          label="Card Expiry"
          value={formatDate(fuelCard.expiry_date)}
          badge={getExpiryBadge(fuelCard.expiry_date, isDisposed)}
        />
        <DetailField label="Issue Date" value={formatDate(fuelCard.issue_date)} />
        {fuelCard.return_date && (
          <DetailField label="Returned Date" value={formatDate(fuelCard.return_date)} />
        )}
      </Section>

      {/* Notes */}
      {fuelCard.notes && (
        <Section title="Notes" icon={FileText}>
          <div className="sm:col-span-2">
            <p className="text-sm text-gray-700 whitespace-pre-wrap">{fuelCard.notes}</p>
          </div>
        </Section>
      )}
    </div>
  );
};

// ============================================
// DIESEL TOKEN SECTION
// ============================================

const DieselTokenSection = ({ dieselToken, asset, onNavigateToAsset }) => {
  if (!dieselToken) return null;

  // The linked vehicle is navigable only when both the API exposes its
  // Asset PK and the modal hosts a drill-in handler. Otherwise we fall back
  // to a read-only display (preserves the employee-view contract too).
  const vehicleLabel = `${dieselToken.linked_vehicle_brand || ''} ${dieselToken.linked_vehicle_model || ''}`.trim() || '—';
  const canNavigateToVehicle = !!(dieselToken.linked_vehicle_asset_id && onNavigateToAsset);

  return (
    <div className="space-y-3">
      {/* Token Details */}
      <Section title="Token Details" icon={Fuel}>
        <DetailField label="Token ID" value={asset?.asset_tag} />
        <DetailField label="Token Tag" value={dieselToken.token_tag} />
        <ToggleablePINField label="PIN" pin={dieselToken.pin} />
      </Section>

      {/* Linked Vehicle */}
      <Section title="Linked Vehicle" icon={Link}>
        {dieselToken.linked_vehicle_number ? (
          canNavigateToVehicle ? (
            <div className="sm:col-span-2">
              <button
                type="button"
                onClick={() => onNavigateToAsset(dieselToken.linked_vehicle_asset_id)}
                title={`Open vehicle ${dieselToken.linked_vehicle_asset_tag || dieselToken.linked_vehicle_number}`}
                aria-label={`Open vehicle ${dieselToken.linked_vehicle_number}`}
                className="group w-full -mx-2 px-2 py-2 rounded-lg border border-transparent bg-transparent hover:bg-white hover:border-gray-200 transition-colors text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2"
              >
                <div className="flex items-center gap-3">
                  <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
                    <div>
                      <span className="block text-xs text-gray-500 font-medium">Vehicle Number</span>
                      <span className="mt-0.5 block text-sm text-gray-900 group-hover:text-red-700 transition-colors">
                        {dieselToken.linked_vehicle_number}
                      </span>
                    </div>
                    <div>
                      <span className="block text-xs text-gray-500 font-medium">Vehicle</span>
                      <span className="mt-0.5 block text-sm text-gray-900">{vehicleLabel}</span>
                    </div>
                  </div>
                  <ExternalLink className="w-4 h-4 text-gray-400 group-hover:text-red-600 transition-colors flex-shrink-0" />
                </div>
              </button>
            </div>
          ) : (
            <>
              <DetailField label="Vehicle Number" value={dieselToken.linked_vehicle_number} />
              <DetailField label="Vehicle" value={vehicleLabel} />
            </>
          )
        ) : (
          <div className="sm:col-span-2 text-sm text-gray-500 italic">
            No vehicle linked
          </div>
        )}
      </Section>

      {/* Notes */}
      {dieselToken.notes && (
        <Section title="Notes" icon={FileText}>
          <div className="sm:col-span-2">
            <p className="text-sm text-gray-700 whitespace-pre-wrap">{dieselToken.notes}</p>
          </div>
        </Section>
      )}
    </div>
  );
};

// ============================================
// DEFAULT SECTION
// ============================================

const DefaultAssetSection = ({ asset, isDisposed = false, isEmployeeView = false }) => (
  <div className="space-y-3">
    {/* Basic Info */}
    <Section title="Asset Details" icon={Package}>
      <DetailField label="Serial Number" value={asset.serial_number} />
      <DetailField label="Brand" value={asset.brand} />
      <DetailField label="Model" value={asset.model} />
      <DetailField label="Condition" value={asset.condition_display} />
      {!isEmployeeView && asset.deployed_project_name && (
        <DetailField
          label="Location"
          value={`${asset.deployed_project_code} — ${asset.deployed_project_name}${asset.deployed_work_site_name ? ` / ${asset.deployed_work_site_name}` : ''}`}
          className="sm:col-span-2"
        />
      )}
      {!isEmployeeView && !asset.deployed_project_name && asset.location && (
        <DetailField label="Location" value={asset.location} className="sm:col-span-2" />
      )}
    </Section>
    
    {/* Warranty - Simplified for employees */}
    {(asset.warranty_start_date || asset.warranty_end_date) && (
      <Section title="Warranty" icon={Shield}>
        {!isEmployeeView && (
          <DetailField label="Start Date" value={formatDate(asset.warranty_start_date)} />
        )}
        <DetailFieldWithBadge 
          label={isEmployeeView ? "Warranty Until" : "End Date"}
          value={formatDate(asset.warranty_end_date)}
          badge={asset.warranty_end_date && getExpiryBadge(asset.warranty_end_date, isDisposed, { variant: 'warranty' })}
        />
        {!isEmployeeView && (
          <DetailField label="Provider" value={asset.warranty_provider} />
        )}
      </Section>
    )}
    
    {/* Purchase - Admin only */}
    {!isEmployeeView && (asset.purchase_date || asset.purchase_price || asset.ownership_type) && (
      <Section title="Purchase Information" icon={CreditCard}>
          <DetailField
            label="Ownership"
            value={asset.ownership_type_display || asset.ownership_type || '—'}
          />
          {asset.lease_return_date && (
            <DetailField label="Returned" value={formatDate(asset.lease_return_date)} />
          )}
          {asset.lease_returned_to && (
            <DetailField label="Returned to" value={asset.lease_returned_to} />
          )}
          <DetailField label="Date" value={formatDate(asset.purchase_date)} />
          <DetailField label="Price" value={formatCurrency(asset.purchase_price)} />
          <DetailField label="Vendor" value={asset.vendor_name || asset.purchase_vendor} />
      </Section>
    )}
  </div>
);

// ============================================
// HISTORY TAB (Employee View - Assignment Summary)
// ============================================

/**
 * Quiet assignment facts for the employee "My Assignment" tab.
 * Status / holder already live in the modal header — this surface adds
 * duration + condition without a second hero or nested-card chrome.
 */
const EmployeeHistoryTab = ({ asset }) => {
  const getTimeSinceAssignment = () => {
    if (!asset?.assigned_date) return null;
    const assignedDate = new Date(asset.assigned_date);
    const now = new Date();
    const diffMs = now - assignedDate;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays < 30) return `${diffDays} ${pluralizeDays(diffDays)}`;
    if (diffDays < 365) {
      const months = Math.floor(diffDays / 30);
      return `${months} month${months > 1 ? 's' : ''}`;
    }
    const years = Math.floor(diffDays / 365);
    const remainingMonths = Math.floor((diffDays % 365) / 30);
    if (remainingMonths > 0) {
      return `${years} year${years > 1 ? 's' : ''}, ${remainingMonths} month${remainingMonths > 1 ? 's' : ''}`;
    }
    return `${years} year${years > 1 ? 's' : ''}`;
  };

  const timeSince = getTimeSinceAssignment();
  const isFleet = asset?.category_code === 'FLT';
  const supportContact = isFleet ? 'your fleet custodian' : 'IT Support';
  const referenceId = isFleet
    ? (asset?.fleet_vehicle?.vehicle_number || asset?.asset_tag)
    : asset?.asset_tag;

  return (
    <div className="space-y-3">
      <div className="bg-gray-50 rounded-xl p-4">
        <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
          Your assignment
        </h4>
        <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3">
          <div>
            <dt className="field-label">Assigned since</dt>
            <dd className="field-display">
              {asset?.assigned_date ? formatDate(asset.assigned_date) : '—'}
            </dd>
          </div>
          {timeSince && (
            <div>
              <dt className="field-label">Time in possession</dt>
              <dd className="field-display">{timeSince}</dd>
            </div>
          )}
          <div>
            <dt className="field-label">Condition</dt>
            <dd className="field-display">{asset?.condition_display || '—'}</dd>
          </div>
        </dl>
      </div>

      <p className="text-xs text-gray-500 px-1">
        Need to report an issue? Contact {supportContact}
        {referenceId ? (
          <>
            {' '}with reference{' '}
            <span className="font-mono font-medium text-gray-700">{referenceId}</span>
          </>
        ) : null}
        .
      </p>
    </div>
  );
};

// ============================================
// ACK LETTER EMAIL STATE (Admin timeline helper)
// ============================================

/**
 * Compact inline indicator + Resend control for the acknowledgement-letter
 * email dispatch state.  Renders different affordances per state:
 *  - sent     : subtle green tick + "Emailed <date>"
 *  - queued   : amber "Queued…" pill (in-flight)
 *  - pending  : amber "Email queuing…" pill (task not yet started)
 *  - failed   : red "Email failed" pill + red Resend button
 *  - skipped  : gray "Email skipped" pill + blue Resend button
 *  - null/unk : muted "Email: —" + blue Resend button
 *
 * Admin-theme focus rings (red).  Never renders if letterId is missing.
 */
const AckLetterEmailState = ({
  emailStatus,
  emailSentAt,
  letterId,
  letterReference,
  resending,
  onResend,
}) => {
  if (!letterId) return null;

  const status = (emailStatus || '').toLowerCase();
  const sentLabel = emailSentAt ? `Emailed ${formatDate(emailSentAt)}` : 'Emailed';

  let pill = null;
  let allowResend = true;

  if (status === 'sent') {
    pill = (
      <span className="inline-flex items-center gap-1 text-xs text-green-700">
        <Mail className="w-3 h-3" />
        <span>{sentLabel}</span>
      </span>
    );
    // Resend is still allowed (e.g., recipient lost the email), but less prominent.
  } else if (status === 'queued' || status === 'pending') {
    pill = (
      <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 text-amber-800 px-2 py-0.5 text-[11px] font-medium">
        <Clock className="w-3 h-3" />
        {status === 'queued' ? 'Queued…' : 'Email queuing…'}
      </span>
    );
    allowResend = false; // avoid double-sends while task is in flight
  } else if (status === 'failed') {
    pill = (
      <span
        className="inline-flex items-center gap-1 rounded-full bg-red-50 text-red-700 px-2 py-0.5 text-[11px] font-medium"
        title="The n8n webhook did not accept this email. Ops has been alerted."
      >
        <MailWarning className="w-3 h-3" />
        Email failed
      </span>
    );
  } else if (status === 'skipped') {
    pill = (
      <span
        className="inline-flex items-center gap-1 rounded-full bg-gray-100 text-gray-700 px-2 py-0.5 text-[11px] font-medium"
        title="Email was skipped (e.g., recipient has no work email)."
      >
        <MailWarning className="w-3 h-3" />
        Email skipped
      </span>
    );
  } else {
    pill = <span className="text-[11px] text-gray-400">Email: —</span>;
  }

  return (
    <span className="inline-flex items-center gap-2">
      {pill}
      {allowResend && onResend && (
        <button
          type="button"
          onClick={onResend}
          disabled={resending}
          className="inline-flex items-center gap-1 text-[11px] font-medium text-red-700 hover:text-red-800 disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-1 rounded"
          title={`Resend ${letterReference || 'acknowledgement letter'} email to the recipient`}
          aria-label={`Resend acknowledgement letter email${letterReference ? ` (${letterReference})` : ''}`}
        >
          <RefreshCw className={`w-3 h-3 ${resending ? 'animate-spin' : ''}`} />
          {resending ? 'Resending…' : 'Resend'}
        </button>
      )}
    </span>
  );
};

const ReceiptLinkEmailState = ({
  status,
  sentAt,
  recipient,
  attempts = 0,
  lastError = '',
  resending,
  onResend,
}) => {
  const normalizedStatus = (status || 'pending').toLowerCase();
  const state = {
    sent: {
      icon: Mail,
      label: sentAt ? `Magic link emailed ${formatDate(sentAt)}` : 'Magic link emailed',
      className: 'bg-green-50 text-green-700',
    },
    queued: {
      icon: Clock,
      label: 'Magic link email queued',
      className: 'bg-orange-50 text-orange-700',
    },
    failed: {
      icon: MailWarning,
      label: 'Magic link email failed',
      className: 'bg-red-50 text-red-700',
    },
    pending: {
      icon: AlertTriangle,
      label: 'Email delivery not verified',
      className: 'bg-orange-50 text-orange-700',
    },
  }[normalizedStatus] || {
    icon: AlertTriangle,
    label: 'Email delivery not verified',
    className: 'bg-orange-50 text-orange-700',
  };
  const StateIcon = state.icon;
  const isQueued = normalizedStatus === 'queued';
  const actionLabel = normalizedStatus === 'failed'
    ? 'Retry email'
    : normalizedStatus === 'sent'
      ? 'Resend link'
      : 'Send new link';

  return (
    <div className="mt-2 rounded-lg border border-gray-200 bg-white p-3">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <span className={`inline-flex items-center gap-1.5 rounded-full px-2 py-1 text-xs font-medium ${state.className}`}>
            <StateIcon className="h-3.5 w-3.5" />
            {state.label}
          </span>
          <p className="mt-1 text-xs text-gray-500">
            {recipient || 'No recipient recorded'}
            {attempts > 0 ? ` · ${attempts} delivery attempt${attempts === 1 ? '' : 's'}` : ''}
          </p>
          {normalizedStatus === 'failed' && lastError && (
            <p className="mt-1 text-xs text-red-600" title={lastError}>
              {lastError}
            </p>
          )}
        </div>
        {onResend && (
          <button
            type="button"
            onClick={onResend}
            disabled={resending || isQueued}
            className="btn btn-sm btn-admin-outline text-xs flex-shrink-0"
            aria-label={`${actionLabel}${recipient ? ` to ${recipient}` : ''}`}
          >
            <RefreshCw className={`h-4 w-4 mr-1.5 ${resending ? 'animate-spin' : ''}`} />
            {resending ? 'Queuing…' : actionLabel}
          </button>
        )}
      </div>
    </div>
  );
};


/**
 * LetterGenerationState — admin pill exposing AssetTransfer.letter_status.
 *
 * Surfaces the *generation* (PDF render) state, distinct from the *email* state
 * shown by AckLetterEmailState. Renders only when a letter is expected for the
 * transfer (completed + acknowledged) AND there is no successfully-generated
 * letter yet, so the timeline doesn't get noisy on healthy rows.
 *
 * States:
 *  - pending    : amber "Letter pending" + Generate button
 *  - generating : amber "Generating..." (no button — task in flight)
 *  - failed     : red "Generation failed" + Retry button (tooltip = last error)
 *  - skipped    : gray "Letter skipped" (tooltip = reason; no button)
 *
 * Admin theme: red focus rings.
 */
const LetterGenerationState = ({
  letterStatus,
  letterAttempts,
  letterLastError,
  regenerating,
  onRegenerate,
}) => {
  const status = (letterStatus || '').toLowerCase();
  if (!status || status === 'generated') return null;

  let pill = null;
  let allowAction = false;
  let actionLabel = 'Generate';

  if (status === 'pending') {
    pill = (
      <span
        className="inline-flex items-center gap-1 rounded-full bg-amber-50 text-amber-800 px-2 py-0.5 text-[11px] font-medium"
        title="Letter has not been generated yet."
      >
        <Clock className="w-3 h-3" />
        Letter pending
      </span>
    );
    allowAction = true;
  } else if (status === 'generating') {
    pill = (
      <span
        className="inline-flex items-center gap-1 rounded-full bg-amber-50 text-amber-800 px-2 py-0.5 text-[11px] font-medium"
        title={`Generation in progress${letterAttempts ? ` (attempt ${letterAttempts})` : ''}.`}
      >
        <Clock className="w-3 h-3 animate-pulse" />
        Generating…
      </span>
    );
  } else if (status === 'failed') {
    const tooltip = letterLastError
      ? `Generation failed: ${letterLastError}`
      : 'Letter generation failed after retries. Ops has been alerted.';
    pill = (
      <span
        className="inline-flex items-center gap-1 rounded-full bg-red-50 text-red-700 px-2 py-0.5 text-[11px] font-medium"
        title={tooltip}
      >
        <AlertTriangle className="w-3 h-3" />
        Letter failed{letterAttempts ? ` (${letterAttempts}×)` : ''}
      </span>
    );
    allowAction = true;
    actionLabel = 'Retry';
  } else if (status === 'skipped') {
    const tooltip = letterLastError
      ? `Skipped: ${letterLastError}`
      : 'Letter generation was skipped for this transfer.';
    pill = (
      <span
        className="inline-flex items-center gap-1 rounded-full bg-gray-100 text-gray-700 px-2 py-0.5 text-[11px] font-medium"
        title={tooltip}
      >
        <FileText className="w-3 h-3" />
        Letter skipped
      </span>
    );
  } else {
    return null;
  }

  return (
    <span className="inline-flex items-center gap-2">
      {pill}
      {allowAction && onRegenerate && (
        <button
          type="button"
          onClick={onRegenerate}
          disabled={regenerating}
          className="inline-flex items-center gap-1 text-[11px] font-medium text-red-700 hover:text-red-800 disabled:opacity-50 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-1 rounded"
          title="Re-fire the generation task"
          aria-label="Regenerate acknowledgement letter"
        >
          <RefreshCw className={`w-3 h-3 ${regenerating ? 'animate-spin' : ''}`} />
          {regenerating ? `${actionLabel}…` : actionLabel}
        </button>
      )}
    </span>
  );
};


// ============================================
// HISTORY TAB (Admin View - Asset Timeline)
// ============================================

const AdminHistoryTab = ({ assetId, asset, readOnly = false }) => {
  const { data: transferHistory = [], isLoading, refetch: refetchHistory } = useAssetHistory(assetId);
  const [letterModal, setLetterModal] = useState({ open: false, transfer: null });
  const [resendingId, setResendingId] = useState(null);
  const [emailStateOverrides, setEmailStateOverrides] = useState({});
  const [resendingReceiptId, setResendingReceiptId] = useState(null);
  const [receiptStateOverrides, setReceiptStateOverrides] = useState({});
  const [regeneratingTransferId, setRegeneratingTransferId] = useState(null);
  const [letterStatusOverrides, setLetterStatusOverrides] = useState({});
  const [historyPage, setHistoryPage] = useState(1);

  const handleResendAckEmail = async (letterId, letterReference) => {
    if (!letterId) return;
    try {
      setResendingId(letterId);
      await assetInventoryApi.resendAssetAcknowledgementLetterEmail(letterId);
      setEmailStateOverrides((prev) => ({
        ...prev,
        [letterId]: { email_status: 'queued', email_sent_at: null },
      }));
      toast.success(`Queued ${letterReference || 'acknowledgement letter'} email for resend`);
      if (typeof refetchHistory === 'function') {
        refetchHistory();
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Failed to resend email.';
      toast.error(detail);
    } finally {
      setResendingId(null);
    }
  };

  const handleResendReceiptLink = async (transferId) => {
    if (!transferId) return;
    try {
      setResendingReceiptId(transferId);
      const response = await assetInventoryApi.resendAssetReceiptLink(transferId);
      setReceiptStateOverrides((prev) => ({
        ...prev,
        [transferId]: {
          status: response.data?.email_status || 'queued',
          recipient: response.data?.recipient || null,
          sentAt: null,
          attempts: 0,
          lastError: '',
        },
      }));
      toast.success(response.data?.detail || 'Magic link email queued');
      if (typeof refetchHistory === 'function') {
        refetchHistory();
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Failed to queue a new magic link.';
      toast.error(detail);
    } finally {
      setResendingReceiptId(null);
    }
  };

  const handleRegenerateLetter = async (transferId, transferReference) => {
    if (!transferId) return;
    try {
      setRegeneratingTransferId(transferId);
      await assetInventoryApi.regenerateAssetAcknowledgementLetter(transferId);
      // Optimistically reflect the queued state until the next refetch lands.
      setLetterStatusOverrides((prev) => ({
        ...prev,
        [transferId]: { letter_status: 'generating' },
      }));
      toast.success(
        `Queued letter generation for ${transferReference || `transfer #${transferId}`}`
      );
      if (typeof refetchHistory === 'function') {
        refetchHistory();
      }
    } catch (err) {
      const detail =
        err?.response?.data?.error ||
        err?.response?.data?.detail ||
        'Failed to queue letter regeneration.';
      toast.error(detail);
    } finally {
      setRegeneratingTransferId(null);
    }
  };
  
  if (isLoading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map(i => (
          <div key={i} className="animate-pulse flex gap-3">
            <div className="w-10 h-10 bg-gray-200 rounded-full"></div>
            <div className="flex-1 space-y-2">
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
              <div className="h-3 bg-gray-200 rounded w-1/2"></div>
            </div>
          </div>
        ))}
      </div>
    );
  }

  // Build timeline events from asset data
  const timelineEvents = [];
  
  // 1. Purchase/Acquisition (oldest event)
  if (asset?.purchase_date) {
    timelineEvents.push({
      type: 'purchase',
      date: asset.purchase_date,
      title: 'Asset Acquired',
      description: (asset.vendor_name || asset.purchase_vendor) ? `Purchased from ${asset.vendor_name || asset.purchase_vendor}` : 'Added to inventory',
      icon: 'purchase',
      color: 'green'
    });
  } else if (asset?.created_at) {
    timelineEvents.push({
      type: 'created',
      date: asset.created_at,
      title: 'Record Created',
      description: 'Asset added to system',
      icon: 'create',
      color: 'gray'
    });
  }

  // 2. Warranty Start
  if (asset?.warranty_start_date) {
    timelineEvents.push({
      type: 'warranty_start',
      date: asset.warranty_start_date,
      title: 'Warranty Started',
      description: asset.warranty_provider ? `Provider: ${asset.warranty_provider}` : 'Warranty period began',
      icon: 'shield',
      color: 'blue'
    });
  }

  // 3. Add transfer history
  transferHistory.forEach(transfer => {
    // Access-control revocations are surfaced as a dedicated, attributed
    // "Access Deactivated" event below (derived from deactivation_date with
    // "Revoked by X"). Skip the raw transfer row here to avoid a duplicate.
    if (transfer.transfer_type === 'access_deactivation') return;
    const letterId = transfer.ack_letter_id || null;
    const override = letterId ? emailStateOverrides[letterId] : null;
    const receiptOverride = receiptStateOverrides[transfer.id];
    const letterOverride = letterStatusOverrides[transfer.id];
    const description = formatTransferRoute(transfer);
    timelineEvents.push({
      type: 'transfer',
      date: transfer.effective_date || transfer.transfer_date,
      title: resolveTransferTypeLabel(transfer) || 'Transfer',
      description,
      location_route: formatTransferLocationRoute(transfer),
      icon: transfer.transfer_type === 'lease_return'
        ? 'lessor'
        : transfer.transfer_type === 'return' ? 'return' : 'transfer',
      color: transfer.transfer_type === 'lease_return'
        ? 'gray'
        : transfer.transfer_type === 'return' ? 'amber' : 'blue',
      transfer_type: transfer.transfer_type,
      to_employee_name: transfer.to_employee_name || null,
      transfer_date: transfer.transfer_date || null,
      status: transfer.status,
      status_display: transfer.status_display,
      transfer_id: transfer.id,
      transfer_reference: transfer.transfer_reference,
      acknowledged_by_recipient: transfer.acknowledged_by_recipient,
      ack_letter_id: letterId,
      ack_letter_reference: transfer.ack_letter_reference || null,
      ack_letter_email_status: override?.email_status ?? transfer.ack_letter_email_status ?? null,
      ack_letter_email_sent_at: override?.email_sent_at ?? transfer.ack_letter_email_sent_at ?? null,
      to_employee_is_external: transfer.to_employee_is_external === true,
      receipt_email_token_id: transfer.receipt_email_token_id || null,
      receipt_email_status: receiptOverride?.status ?? transfer.receipt_email_status ?? null,
      receipt_email_sent_at: receiptOverride?.sentAt ?? transfer.receipt_email_sent_at ?? null,
      receipt_email_attempts: receiptOverride?.attempts ?? transfer.receipt_email_attempts ?? 0,
      receipt_email_last_error: receiptOverride?.lastError ?? transfer.receipt_email_last_error ?? '',
      receipt_email_recipient: receiptOverride?.recipient ?? transfer.receipt_email_recipient ?? null,
      letter_status: letterOverride?.letter_status ?? transfer.letter_status ?? null,
      letter_attempts: transfer.letter_attempts ?? 0,
      letter_last_error: transfer.letter_last_error ?? '',
      transfer_asset_tag: transfer.asset_tag || null,
    });
  });

  // 4. Current Assignment (if assigned and not in transfers)
  if (asset?.assigned_date && asset?.current_assignee_name) {
    const hasAssignmentTransfer = transferHistory.some(t => 
      t.to_employee_name === asset.current_assignee_name && 
      t.status === 'completed'
    );
    
    if (!hasAssignmentTransfer) {
      timelineEvents.push({
        type: 'assignment',
        date: asset.assigned_date,
        title: 'Assigned to Employee',
        description: formatEmployeeParty(
          asset.current_assignee_name,
          asset.current_assignee_staff_id,
        ),
        icon: 'user',
        color: 'blue'
      });
    }
  }

  // 5. Activation (for door access)
  if (asset?.activation_date) {
    timelineEvents.push({
      type: 'activation',
      date: asset.activation_date,
      title: 'Access Activated',
      description: 'Employee registered in access system',
      icon: 'key',
      color: 'green'
    });
  }

  // 6. Deactivation (for door access)
  if (asset?.deactivation_date) {
    timelineEvents.push({
      type: 'deactivation',
      date: asset.deactivation_date,
      title: 'Access Deactivated',
      description: asset.deactivated_by_name
        ? `Revoked by ${asset.deactivated_by_name}${asset.deactivated_by_staff_id ? ` (${asset.deactivated_by_staff_id})` : ''}`
        : 'Automated deactivation (employment ended)',
      icon: 'key',
      color: 'red'
    });
  }

  // 7. Disposal
  if (asset?.disposal_date) {
    timelineEvents.push({
      type: 'disposal',
      date: asset.disposal_date,
      title: 'Asset Disposed',
      description: asset.disposal_method || 'Removed from service',
      icon: 'disposal',
      color: 'red'
    });
  }

  // 7b. Subscription / contract terminated (non-disposal lifecycle end)
  if (asset?.lifecycle_end_date) {
    timelineEvents.push({
      type: 'terminated',
      date: asset.lifecycle_end_date,
      title: 'Subscription / Contract Ended',
      description: asset.lifecycle_end_reason || 'No longer active',
      icon: 'disposal',
      color: 'gray'
    });
  }

  // Sort by precise instant (newest first) so same-day events order by time.
  timelineEvents.sort((a, b) => eventInstant(b) - eventInstant(a));

  // Client-side pagination over the full (sorted) timeline. Duration math still
  // references the full array by global index, so windowing the render doesn't
  // distort the "held for" segment durations.
  const HISTORY_PAGE_SIZE = 6;
  const totalHistoryPages = Math.max(1, Math.ceil(timelineEvents.length / HISTORY_PAGE_SIZE));
  const currentHistoryPage = Math.min(historyPage, totalHistoryPages);
  const historyStart = (currentHistoryPage - 1) * HISTORY_PAGE_SIZE;
  const historyEnd = historyStart + HISTORY_PAGE_SIZE;

  const getIconComponent = (iconType, colorClass) => {
    const colorMap = {
      green: 'text-green-600 bg-green-100',
      blue: 'text-blue-600 bg-blue-100',
      amber: 'text-amber-600 bg-amber-100',
      red: 'text-red-600 bg-red-100',
      gray: 'text-gray-600 bg-gray-100',
    };
    const colors = colorMap[colorClass] || colorMap.gray;
    
    return (
      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${colors}`}>
        {iconType === 'purchase' && <CreditCard className="w-5 h-5" />}
        {iconType === 'create' && <Package className="w-5 h-5" />}
        {iconType === 'shield' && <Shield className="w-5 h-5" />}
        {iconType === 'transfer' && <User className="w-5 h-5" />}
        {iconType === 'return' && <MapPin className="w-5 h-5" />}
        {iconType === 'lessor' && <Building2 className="w-5 h-5" />}
        {iconType === 'user' && <User className="w-5 h-5" />}
        {iconType === 'key' && <Key className="w-5 h-5" />}
        {iconType === 'disposal' && <AlertCircle className="w-5 h-5" />}
      </div>
    );
  };

  if (timelineEvents.length === 0) {
    return (
      <div className="text-center py-8">
        <Clock className="w-12 h-12 text-gray-300 mx-auto mb-3" />
        <p className="text-gray-500">No history available</p>
        <p className="text-xs text-gray-400 mt-1">
          Asset events and transfers will appear here
        </p>
      </div>
    );
  }
  
  return (
    <div className="space-y-3">
      {/* Summary Stats */}
      <div className="grid grid-cols-3 gap-2 mb-3">
        <div className="bg-gray-50 rounded-lg p-2 text-center">
          <p className="text-xl font-bold text-gray-900">{transferHistory.length}</p>
          <p className="text-xs text-gray-500">Transfers</p>
        </div>
        <div className="bg-gray-50 rounded-lg p-2 text-center">
          <p className="text-xl font-bold text-gray-900">
            {formatAgeYearsMonths(asset?.purchase_date, { short: true })}
          </p>
          <p className="text-xs text-gray-500">Asset Age</p>
        </div>
        <div className="bg-gray-50 rounded-lg p-2 text-center">
          <p className="text-xl font-bold text-gray-900">{timelineEvents.length}</p>
          <p className="text-xs text-gray-500">Events</p>
        </div>
      </div>

      {letterModal.open && letterModal.transfer?.ack_letter_id && (
        <LetterPreviewModal
          letterId={letterModal.transfer.ack_letter_id}
          letterReference={letterModal.transfer.ack_letter_reference}
          assetTag={letterModal.transfer.transfer_asset_tag || asset?.asset_tag}
          onClose={() => setLetterModal({ open: false, transfer: null })}
          theme="admin"
          allLettersHref={null}
        />
      )}

      {/* Timeline */}
      <div className="relative">
        {/* Vertical line */}
        <div className="absolute left-5 top-0 bottom-0 w-0.5 bg-gray-200"></div>
        
        <div className="space-y-3">
          {timelineEvents.map((event, idx) => {
            if (idx < historyStart || idx >= historyEnd) return null;
            return (
            <div key={idx} className="flex gap-4 relative">
              {/* Icon */}
              <div className="relative z-10">
                {getIconComponent(event.icon, event.color)}
              </div>
              
              {/* Content */}
              <div className="flex-1 bg-gray-50 rounded-lg p-3 -mt-1">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-900">{event.title}</p>
                    <p className="text-xs text-gray-500">{event.description}</p>
                    {event.location_route && (
                      <p className="text-xs text-gray-400">{event.location_route}</p>
                    )}
                    {(() => {
                      const seg = getSegmentMeta(event);
                      if (!seg) return null;
                      const nextDate = idx === 0 ? new Date() : eventInstant(timelineEvents[idx - 1]);
                      const duration = formatDurationShort(eventInstant(event), nextDate);
                      if (!duration) return null;
                      const toneMap = {
                        blue: 'bg-blue-50 text-blue-700',
                        amber: 'bg-amber-50 text-amber-700',
                        gray: 'bg-gray-100 text-gray-600',
                      };
                      return (
                        <span
                          className={`mt-1.5 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${toneMap[seg.tone] || toneMap.gray}`}
                          title={idx === 0 ? 'Current duration (ongoing)' : 'Duration until the next event'}
                        >
                          <Clock className="w-3 h-3" />
                          {seg.verb} · {duration}{idx === 0 ? ' · ongoing' : ''}
                        </span>
                      );
                    })()}
                    {event.type === 'transfer' &&
                      event.status === 'pending_receipt' &&
                      event.to_employee_is_external && (
                        <ReceiptLinkEmailState
                          status={event.receipt_email_status}
                          sentAt={event.receipt_email_sent_at}
                          recipient={event.receipt_email_recipient}
                          attempts={event.receipt_email_attempts}
                          lastError={event.receipt_email_last_error}
                          resending={resendingReceiptId === event.transfer_id}
                          onResend={
                            readOnly
                              ? undefined
                              : () => handleResendReceiptLink(event.transfer_id)
                          }
                        />
                      )}
                    {event.ack_letter_id && !WORKSHOP_MOVE_TYPES.includes(event.transfer_type) && (
                      <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1">
                        <button
                          onClick={() => setLetterModal({ open: true, transfer: event })}
                          className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-700 font-medium focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-1 rounded"
                          title={`View acknowledgement letter${event.ack_letter_reference ? ` (${event.ack_letter_reference})` : ''}`}
                        >
                          <FileText className="w-3 h-3" />
                          {event.ack_letter_reference || 'View letter'}
                        </button>
                        <AckLetterEmailState
                          emailStatus={event.ack_letter_email_status}
                          emailSentAt={event.ack_letter_email_sent_at}
                          letterId={event.ack_letter_id}
                          letterReference={event.ack_letter_reference}
                          resending={resendingId === event.ack_letter_id}
                          onResend={readOnly ? undefined : () => handleResendAckEmail(event.ack_letter_id, event.ack_letter_reference)}
                        />
                      </div>
                    )}
                    {/* Letter generation state — only shown for completed+acknowledged transfers
                        with NO letter row yet, so admins can see/recover stuck/failed renders. */}
                    {!event.ack_letter_id &&
                      event.type === 'transfer' &&
                      !WORKSHOP_MOVE_TYPES.includes(event.transfer_type) &&
                      event.status === 'completed' &&
                      event.acknowledged_by_recipient &&
                      event.letter_status &&
                      event.letter_status !== 'generated' && (
                      <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1">
                        <LetterGenerationState
                          letterStatus={event.letter_status}
                          letterAttempts={event.letter_attempts}
                          letterLastError={event.letter_last_error}
                          regenerating={regeneratingTransferId === event.transfer_id}
                          onRegenerate={readOnly ? undefined : () => handleRegenerateLetter(event.transfer_id, event.transfer_reference)}
                        />
                      </div>
                    )}
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-gray-400">{formatDate(event.date)}</p>
                    {event.status_display && (
                      <span className="inline-block mt-1">
                        <StatusBadge status={event.status} label={event.status_display} />
                      </span>
                    )}
                  </div>
                </div>
              </div>
            </div>
            );
          })}
        </div>
      </div>

      <Pagination
        currentPage={currentHistoryPage}
        totalPages={totalHistoryPages}
        onPageChange={setHistoryPage}
        totalCount={timelineEvents.length}
        pageSize={HISTORY_PAGE_SIZE}
        theme="admin"
        noun="events"
      />
    </div>
  );
};

// ============================================
// RETURN RECEIPT PHOTOS SECTION
// ============================================

// Helper function to get photo type chip
const getPhotoTypeChip = (photoType, photoTypeDisplay) => {
  if (!photoType || !photoTypeDisplay) {
    return (
      <span className="inline-flex items-center px-2.5 py-1 bg-gray-50 text-gray-700 text-xs font-medium rounded-full">
        Unknown
      </span>
    );
  }

  // Color coding based on photo type
  let bgColor, textColor;
  if (photoType === 'return') {
    bgColor = 'bg-blue-50';
    textColor = 'text-blue-700';
  } else if (photoType === 'transfer_received') {
    bgColor = 'bg-green-50';
    textColor = 'text-green-700';
  } else {
    // Fallback for other types
    bgColor = 'bg-gray-50';
    textColor = 'text-gray-700';
  }

  return (
    <span className={`inline-flex items-center px-2.5 py-1 ${bgColor} ${textColor} text-xs font-medium rounded-full`}>
      {photoTypeDisplay}
    </span>
  );
};

const ReturnReceiptPhotosSection = ({ photos = [], isEmployeeView = false }) => {
  const sectionTitle = isEmployeeView ? 'Transaction Photos' : 'Receipt Photos';
  const emptyMessage = isEmployeeView
    ? 'No transaction photos available.'
    : 'No receipt photos available.';
  const theme = isEmployeeView ? 'employee' : 'admin';

  if (!photos || photos.length === 0) {
    return (
      <div className="bg-gray-50 rounded-xl p-3">
        <h4 className="flex items-center gap-2 text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
          <Image className="w-3.5 h-3.5" />
          {sectionTitle}
        </h4>
        <p className="text-xs text-gray-400 text-center py-2">
          {emptyMessage}
        </p>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 rounded-xl p-3">
      <h4 className="flex items-center gap-2 text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
        <Image className="w-3.5 h-3.5" />
        {sectionTitle}
        <span className="ml-auto text-xs font-normal text-gray-400 normal-case">
          {photos.length} photo{photos.length !== 1 ? 's' : ''}
        </span>
      </h4>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {photos.map((photo) => (
          <SecurePhotoThumb
            key={photo.id}
            photoUrl={photo.photo_url || photo.photo}
            alt={photo.caption || (isEmployeeView ? 'Transaction photo' : 'Receipt photo')}
            theme={theme}
          >
            <div className="absolute top-2 left-2 z-10">
              {getPhotoTypeChip(photo.photo_type, photo.photo_type_display)}
            </div>
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-2 opacity-80 group-hover:opacity-100 transition-opacity">
              <p className="text-xs text-white truncate">
                {photo.uploaded_by_name || 'Unknown'}
              </p>
              <p className="text-xs text-white/70">
                {photo.uploaded_at ? formatDate(photo.uploaded_at) : ''}
              </p>
            </div>
          </SecurePhotoThumb>
        ))}
      </div>
    </div>
  );
};

// ============================================
// CATEGORY ICON COMPONENT
// ============================================

const CategoryIcon = ({ code, className = "w-6 h-6" }) => {
  const icons = {
    IT: Laptop,
    ACC: Key,
    FLT: Car,
    LIC: FileKey,
    OFF: Package,
    FUEL: CreditCard,
    DSL: Fuel,
    TEL: Smartphone,
  };
  const Icon = icons[code] || Package;
  return <Icon className={className} />;
};

// ============================================
// MAIN MODAL COMPONENT
// ============================================

const AssetDetailModal = ({ isOpen, onClose, assetId, initialAsset, isEmployeeView = false, readOnly = false }) => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');

  // Drill-in navigation stack. Each entry is { id, parentTag } where
  // parentTag is the asset_tag of the entry directly *below* it (used to
  // label the back button so users always know what they'll return to).
  // - Root: stack length 1, parentTag null (back button hidden).
  // - The X / Escape closes the entire modal; the back button only pops one
  //   level. This matches the iOS-style "navigation controller" pattern that
  //   users already expect from drill-in flows.
  const [stack, setStack] = useState(() => [{ id: assetId, parentTag: null }]);

  // Reset the stack on every (re)open so a closed-and-reopened modal always
  // starts at depth 1, even if the user previously drilled in. Also resets
  // when the parent supplies a different root assetId without closing first.
  useEffect(() => {
    if (isOpen) {
      setStack([{ id: assetId, parentTag: null }]);
      setActiveTab('overview');
    }
  }, [assetId, isOpen]);

  const top = stack[stack.length - 1];
  const effectiveAssetId = top?.id ?? assetId;
  const isAtRoot = stack.length <= 1;

  // initialAsset is a render-time shortcut from the caller (avoids a fetch
  // on the very first render). It only describes the *root* asset, so it
  // must be ignored once the user has drilled into a different asset.
  const useInitial = isAtRoot && !!initialAsset;
  const { data: fetchedAsset, isLoading: loading, error: fetchError } = useAsset(
    useInitial ? undefined : effectiveAssetId
  );
  const asset = useInitial ? initialAsset : fetchedAsset;
  const isLoading = useInitial ? false : loading;
  const error = useInitial ? null : fetchError;

  const pushAsset = useCallback((nextId) => {
    if (!nextId || nextId === effectiveAssetId) return;
    setStack((s) => [...s, { id: nextId, parentTag: asset?.display_name || asset?.asset_tag || null }]);
    setActiveTab('overview');
  }, [effectiveAssetId, asset?.display_name, asset?.asset_tag]);

  const popAsset = useCallback(() => {
    setStack((s) => (s.length > 1 ? s.slice(0, -1) : s));
    setActiveTab('overview');
  }, []);

  if (!isOpen) return null;
  
  const categoryCode = asset?.category_code;
  // True disposal banner: only when the asset was actually disposed (sold,
  // scrapped, written off, etc.). A terminated subscription is NOT a disposal.
  const isTrueDisposal = DISPOSAL_STATUSES.includes(asset?.status);
  const isTerminated = asset?.status === 'terminated';
  const showReturnedBanner = LEASE_RETURN_STATUSES.includes(asset?.status);
  const showDisposedBanner = !!(
    isTrueDisposal || asset?.is_disposed || (asset?.disposal_date && !isTerminated && !showReturnedBanner)
  );
  const showTerminatedBanner = (isTerminated || !!asset?.lifecycle_end_date) && !showReturnedBanner;
  const colors = getCategoryColorByCode(categoryCode);

  // Header meta: Status → holder → site → Since.
  // Employee self-view omits own name/staff ID (already known to the viewer).
  const headerSinceDate = asset?.deployed_date || asset?.assigned_date;
  const isSelfAssignee = Boolean(
    isEmployeeView
    && user?.staff_id
    && asset?.current_assignee_staff_id
    && asset.current_assignee_staff_id === user.staff_id
  );
  const headerMeta = getAssetHeaderMetaVisibility({
    deployedProjectName: asset?.deployed_project_name,
    currentAssigneeName: asset?.current_assignee_name,
    isSelfAssignee,
    sinceDate: headerSinceDate,
  });
  const showDeviceContext = isDeviceMobileLine(asset) && Boolean(deviceLineContext(asset));
  const {
    showHeaderProject,
    showHeaderHolder,
    showHeaderSince,
  } = headerMeta;
  const showHeaderMetaRow = headerMeta.showHeaderMetaRow || showDeviceContext;

  // Fleet vehicles (admin view) get dedicated condition + custody tabs:
  // Maintenance (workshop), Inspection (monthly walk-around), Accidents,
  // Traffic Offences (notices against the plate), Transaction (custody).
  const isFleetAdmin = !isEmployeeView && categoryCode === 'FLT';
  const tabs = isFleetAdmin
    ? [
        { id: 'overview', label: 'Overview' },
        { id: 'maintenance', label: 'Maintenance History' },
        { id: 'inspections', label: 'Inspection History' },
        { id: 'accidents', label: 'Accidents' },
        { id: 'offences', label: 'Traffic Offences' },
        { id: 'transactions', label: 'Transaction History' },
      ]
    : [
        { id: 'overview', label: 'Overview' },
        { id: 'history', label: isEmployeeView ? 'My Assignment' : 'History' },
      ];

  // Render category-specific content
  const renderCategoryContent = () => {
    if (!asset) return null;

    // Treat all inactive lifecycle endpoints (disposed AND terminated/
    // deactivated) the same way for "hide countdown badges" purposes.
    const isDisposed = INACTIVE_STATUSES.includes(asset.status);
    
    const categorySection = (() => {
      switch (categoryCode) {
        case 'FLT':
          return (
            <FleetVehicleSection
              fleet={asset.fleet_vehicle}
              asset={asset}
              isDisposed={isDisposed}
              isEmployeeView={isEmployeeView}
              canSyncOps={!isEmployeeView && !readOnly}
            />
          );
        case 'LIC':
          return (
            <SoftwareLicenseSection 
              license={asset.software_license} 
              seatAssignments={asset.license_seat_assignments}
              isDisposed={isDisposed}
            />
          );
        case 'IT':
          return <ITEquipmentSection asset={asset} isDisposed={isDisposed} isEmployeeView={isEmployeeView} />;
        case 'ACC':
          return <AccessControlSection asset={asset} />;
        case 'FUEL':
          return (
            <FuelCardSection
              fuelCard={asset.fuel_card}
              asset={asset}
              isDisposed={isDisposed}
              onNavigateToAsset={isEmployeeView ? undefined : pushAsset}
            />
          );
        case 'DSL':
          return (
            <DieselTokenSection
              dieselToken={asset.diesel_token}
              asset={asset}
              onNavigateToAsset={isEmployeeView ? undefined : pushAsset}
            />
          );
        case 'TEL':
          return (
            <MobileLineSection
              line={asset.mobile_line}
              asset={asset}
              isDisposed={isDisposed}
              isEmployeeView={isEmployeeView}
            />
          );
        default:
          return <DefaultAssetSection asset={asset} isDisposed={isDisposed} isEmployeeView={isEmployeeView} />;
      }
    })();
    
    return (
      <div className="space-y-3">
        {categorySection}
        {/* Transaction/Receipt Photos Section */}
        {asset.receipt_photos && asset.receipt_photos.length > 0 && (
          <ReturnReceiptPhotosSection photos={asset.receipt_photos} isEmployeeView={isEmployeeView} />
        )}
        {/* QR Code Section */}
        <QRCodeDisplay 
          assetId={asset.id} 
          assetTag={asset.asset_tag}
          showPrintButton={true}
        />
      </div>
    );
  };
  
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      theme={isEmployeeView ? 'employee' : 'admin'}
      customHeader={
        <>
          {/* Header */}
          <div className="p-4 border-b flex-shrink-0">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                {!isAtRoot && (
                  <button
                    type="button"
                    onClick={popAsset}
                    className="icon-button-admin icon-button-sm"
                    title={top?.parentTag ? `Back to ${top.parentTag}` : 'Back'}
                    aria-label={top?.parentTag ? `Back to ${top.parentTag}` : 'Back'}
                  >
                    <ChevronLeft className="w-3.5 h-3.5" />
                  </button>
                )}
                {isLoading ? (
                  <div className="w-10 h-10 bg-gray-200 rounded-full animate-pulse"></div>
                ) : (
                  <div className={`w-10 h-10 ${colors.bg} rounded-full flex items-center justify-center`}>
                    <CategoryIcon code={categoryCode} className={`w-5 h-5 ${colors.text}`} />
                  </div>
                )}
                <div>
                  {isLoading ? (
                    <>
                      <div className="h-5 bg-gray-200 rounded w-32 animate-pulse mb-1.5"></div>
                      <div className="h-3.5 bg-gray-200 rounded w-48 animate-pulse"></div>
                    </>
                  ) : (
                    <>
                      <div className="flex items-center gap-1.5 min-w-0">
                        <h2 className="text-base font-semibold text-gray-900 truncate">
                          {asset?.display_name || asset?.asset_tag}
                        </h2>
                        {isDeviceMobileLine(asset) && (
                          <InfoBadge
                            color="blue"
                            size="xs"
                            label="Device"
                            title="Accountable staff only. This number stays in a device."
                          />
                        )}
                      </div>
                      <p className="text-xs text-gray-500">
                        {asset?.brand} {asset?.model}
                        {asset?.category_name && ` • ${asset.category_name}`}
                      </p>
                    </>
                  )}
                </div>
              </div>
              <button
                onClick={onClose}
                className={isEmployeeView ? 'modal-close' : 'modal-close-admin'}
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Status → holder → site → Since. Self-view omits own identity. */}
            {!isLoading && asset && (
              <div className="mt-2 flex flex-col gap-1.5">
                <div className="flex-shrink-0">
                  <StatusBadge status={asset.status} showIcon />
                </div>

                {showHeaderMetaRow && (
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-gray-600">
                    {showHeaderHolder && (
                      <div className="inline-flex items-center gap-1.5 min-w-0">
                        <User className="w-4 h-4 flex-shrink-0 text-gray-400" />
                        <span className="truncate">{asset.current_assignee_name}</span>
                        {asset.current_assignee_staff_id && (
                          <ClassificationBadge
                            classification={asset.current_assignee_employment_classification}
                            label={asset.current_assignee_staff_id}
                            variant="solid"
                          />
                        )}
                      </div>
                    )}

                    {isDeviceMobileLine(asset) && deviceLineContext(asset) && (
                      <div className="inline-flex items-center gap-1.5 min-w-0">
                        <Smartphone className="w-4 h-4 flex-shrink-0 text-blue-600" />
                        <span className="truncate text-blue-700">{deviceLineContext(asset)}</span>
                      </div>
                    )}

                    {showHeaderProject && (
                      <div className="inline-flex items-center gap-1.5 min-w-0">
                        <MapPin className="w-4 h-4 flex-shrink-0 text-gray-400" />
                        <span className="truncate">{asset.deployed_project_name}</span>
                        {asset.deployed_project_code && (
                          <InfoBadge color="indigo" label={asset.deployed_project_code} />
                        )}
                      </div>
                    )}

                    {showHeaderSince && (
                      <div className="inline-flex items-center gap-1.5 text-gray-500">
                        <Calendar className="w-4 h-4 flex-shrink-0" />
                        <span>Since {formatDate(headerSinceDate)}</span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Leased asset handed back to the lessor (not a disposal) */}
          {showReturnedBanner && asset && (
            <div className="mx-4 mb-0 mt-2 p-3 rounded-lg bg-slate-50 border border-slate-200 flex items-center gap-2">
              <Building2 className="w-5 h-5 text-slate-500 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-slate-800">Returned to the lessor</p>
                <p className="text-xs text-slate-600 mt-0.5">
                  {asset.lease_return_date && (
                    <>Returned {formatDate(asset.lease_return_date)}</>
                  )}
                  {asset.lease_returned_to && (
                    <>
                      {asset.lease_return_date ? ' · ' : ''}
                      {asset.lease_returned_to}
                    </>
                  )}
                </p>
              </div>
            </div>
          )}

          {/* Disposed banner (sold / scrapped / written off / disposed) */}
          {showDisposedBanner && asset && (
            <div className="mx-4 mb-0 mt-2 p-3 rounded-lg bg-amber-50 border border-amber-200 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-amber-800">This asset has been disposed</p>
                {asset.disposal_date && (
                  <p className="text-xs text-amber-700 mt-0.5">Disposal date: {formatDate(asset.disposal_date)}</p>
                )}
              </div>
            </div>
          )}

          {/* Terminated banner: subscription / contract ended (NOT a disposal) */}
          {showTerminatedBanner && !showDisposedBanner && asset && (
            <div className="mx-4 mb-0 mt-2 p-3 rounded-lg bg-slate-50 border border-slate-200 flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-slate-500 flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-slate-800">
                  Subscription / contract ended
                </p>
                <p className="text-xs text-slate-600 mt-0.5">
                  {asset.lifecycle_end_date && (
                    <>End date: {formatDate(asset.lifecycle_end_date)}</>
                  )}
                  {asset.lifecycle_end_reason && (
                    <>
                      {asset.lifecycle_end_date ? ' \u2022 ' : ''}
                      {asset.lifecycle_end_reason}
                    </>
                  )}
                </p>
              </div>
            </div>
          )}

          {/* Tabs - Pill Style. Fleet vehicles (admin) split history into
              Maintenance (workshop), Inspection (monthly walk-around), and
              Transaction (custody) chapters. */}
          <div className="border-b flex-shrink-0">
            <div className="flex items-center gap-2 px-4 py-2 min-w-0 overflow-x-auto md:flex-wrap md:overflow-x-visible scrollbar-hide scroll-smooth">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`${isEmployeeView ? 'tab-btn' : 'tab-btn-admin'} ${activeTab === tab.id ? 'active' : ''}`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
        </>
      }
      footer={!isLoading && asset ? (
        <div className="flex items-center justify-between text-xs text-gray-500">
          <span>Created: {formatDate(asset.created_at)}</span>
          <span>Last updated: {formatDate(asset.updated_at)}</span>
        </div>
      ) : undefined}
      contentClassName="p-4"
    >
      {error ? (
        <div className="text-center py-8">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
          <p className="text-gray-500">Failed to load asset details</p>
        </div>
      ) : isLoading ? (
        <div className="space-y-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="bg-gray-50 rounded-xl p-4 animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-24 mb-4"></div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <div className="h-3 bg-gray-200 rounded w-16"></div>
                  <div className="h-4 bg-gray-200 rounded w-24"></div>
                </div>
                <div className="space-y-2">
                  <div className="h-3 bg-gray-200 rounded w-16"></div>
                  <div className="h-4 bg-gray-200 rounded w-32"></div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : activeTab === 'overview' ? (
        renderCategoryContent()
      ) : activeTab === 'maintenance' ? (
        <FleetMaintenanceTab
          fleet={asset?.fleet_vehicle}
          asset={asset}
          isDisposed={INACTIVE_STATUSES.includes(asset?.status)}
        />
      ) : activeTab === 'inspections' ? (
        <FleetInspectionHistoryTab fleet={asset?.fleet_vehicle} />
      ) : activeTab === 'accidents' ? (
        <FleetAccidentHistoryTab fleet={asset?.fleet_vehicle} />
      ) : activeTab === 'offences' ? (
        <FleetOffenceHistoryTab fleet={asset?.fleet_vehicle} />
      ) : activeTab === 'transactions' ? (
        <AdminHistoryTab assetId={effectiveAssetId} asset={asset} readOnly={readOnly} />
      ) : isEmployeeView ? (
        <EmployeeHistoryTab asset={asset} />
      ) : (
        <AdminHistoryTab assetId={effectiveAssetId} asset={asset} readOnly={readOnly} />
      )}
    </Modal>
  );
};

export default AssetDetailModal;
